// app/customs/spreadtable/index.tsx
'use client';

import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from "react-dom";
import axios from 'axios';
import * as Global from '@utils/global';
import './index.css';
import * as AuthContext from '@/utils/authcontext';
import { useAgentView } from '@/utils/agentviewcontext';
import ErrorMessage from '@/customs/errormessage';
import { useRouter } from 'next/navigation';
import AiSupport from '@/customs/aisupport';
import VoiceRecording from '@/customs/voicerecording';
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
import { ja } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PlayerVoice from '../playervoice';
import WaitingSpinner from '@/customs/waitingspinner';
import BinaryViewer from '@/customs/binaryviewer';

registerLocale("ja", ja);

type SpreadTableProps = {
  spread: string;
  sort?: RequestSort;
  base_filters?: Record<string, any>;
  ui_filters?: Record<string, any>;
  form_pkey?: string | Global.FormPkeyState;
  form_cursor_type?: string | Global.FormCursorType;
  output_type?: string | Global.OutputType;
  direct_form?: boolean;
  onRowClick?: (row: any) => void;
  onClose?: () => void;
};

type RequestSort = {
    sort_key?: string;
    sort_desc?: boolean;
};

type SpreadBulkInsertConfig = {
  enabled: boolean;
  button_title?: string;
};

type ContextHeaderField = {
  label: string;
  value: any;
  length?: number;
  inline?: boolean;
};

type ContextHeader = {
  title: string;
  fields: ContextHeaderField[];
};

type SpreadSheetResponse = {
  meta?: {
    spread: string;
    display?: string;
    pkey?: string;
    columns: ColumnMeta[];
    user_allow: Global.MetaUserAllow;
    show_csv_button: boolean;
    new_button_disabled?: boolean;
    del_button_disabled?: boolean;
    bulk_insert?: SpreadBulkInsertConfig;
    context_headers?: ContextHeader[];
  };
  rows: any[];
};

type FetchSpreadSheetResult = {
  meta: SpreadSheetResponse["meta"] | null;
  rows: any[];
};

type SpreadMetaResponse = {
  meta: {
    spread: string;
    display?: string;
    pkey?: string;
    user_allow: Global.MetaUserAllow;
    new_button_disabled?: boolean;
    del_button_disabled?: boolean;
    columns: ColumnMeta[];
    bulk_insert?: SpreadBulkInsertConfig;
    context_headers?: ContextHeader[];
  };
};

type UpdateRowResponse = {
  ok: boolean;
  key_value: string;
  row: Record<string, any>;
  meta: {
    spread: string;
    columns: ColumnMeta[];
  };
};

type SpreadRowResponse = {
  row: Record<string, any>;
  meta: {
    spread: string;
    display: string;
    pkey: string;
    user_allow: Global.MetaUserAllow;
    columns: ColumnMeta[];
    context_headers?: ContextHeader[];
  };
};

type ColumnMeta = {
  colname: string;
  display: string;
  celldata: string;
  type: string;
  required?: boolean;
  required_when?: ColumnCondition;
  readonly?: boolean;
  readonly_when?: ColumnCondition;
  hidden_when?: ColumnCondition;
  list_hidden?: boolean;
  form_hidden?: boolean;
  defvalue?: string;
  updvalue?: string;
  initvalue?: string;
  length?: number;
  depth?: number;
  cols?: number;
  rows?: number;
  col_backcolor?: string;
  forecolor?: string;
  forecolor_when?: ColumnCondition;
  ui_filter?: boolean;
  ui_filter_required?: boolean;
  inline?: boolean;
  fixed_left?: boolean;
  list_align?: number | string;
  voiceplayer?: string;
  labelvalue?: string;
  options?: { value: any; label: string }[];
  link?:ColumnLinkMeta;
  // STEPフォーム用・UI一時情報
  temp_uid?: string;
  temp_start_at?: number;
  temp_end_at?: number;
};

const getListColumnKey = (col: ColumnMeta, index: number) =>
  `${col.colname || col.celldata || col.display || "col"}-${index}`;

type ColumnCondition = {
  celldata: string;
  values?: any[];
  not_values?: any[];
  clear?: boolean;
  forecolor?: string;
};

type ColumnLinkMeta = {
  // action?: string;
  action?: {
    action_type: string;
    action_template: string;
    // voicerecording で使用中だが、action_pkey でいいはず
    action_filters?: Record<string, any>;
    // 本来これだけでいい
    action_pkey?: string;
  };
  spread?: string;
  base_filters?: Record<string, any>;
  ui_filters?: Record<string, any>;
  form_pkey?: string | Global.FormPkeyState;
  form_cursor_type?: string | Global.FormCursorType;
  output_type?: string | Global.OutputType;
  relay_delete?: boolean;
  stored_proc?: {
    proc_name: string;
    proc_params: Record<string, string>;
  };
};

type ActionCtx = {
  action_type?: string;
  action_template?: string;
  action_filters?: Record<string, any>;
  action_pkey?: any;
};

type ChildCtx = {
  base_filters: Record<string, any>;
  ui_filters?: Record<string, any>;
  form_pkey: string | Global.FormPkeyState;       // 必須にする（NONEを入れる）
  form_cursor_type?: string | Global.FormCursorType;
  output_type?: string | Global.OutputType;
  __action?: ActionCtx;
};


const SpreadTable: React.FC<SpreadTableProps> = (props) => {

  const router = useRouter();
  const { authToken, employeeDomain } = AuthContext.useAuth();
  const { setAgentViewContext, clearAgentViewContext } = useAgentView();
  const agentViewOwnerIdRef = useRef(`spreadtable-${Math.random().toString(36).slice(2)}`);
  const baseTokenInterface = () => Global.createBaseTokenInterface(authToken, employeeDomain);
  const [resolvedProps, setResolvedProps] = useState<SpreadTableProps | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  //alert(JSON.stringify(resolvedProps));
  const [displayMessage, setDisplayMessage] = useState<string | null>(null);
  const [currentPkey] = useState(props.form_pkey);
  const formCursorType =  props.form_cursor_type ?? Global.FormCursorType.ALL_CONFIRM;
  const outputType =  props.output_type ?? Global.OutputType.SCREEN;
  // const formCursorType =  props.form_cursor_type ?? Global.FormCursorType.STEP_FORWARD_ONLY;
  const [curDispColIdx, setCurDispColIdx] = useState(0);
  // const isIOS = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent);
  // const [isIOS, setIsIOS] = useState(false);
  const [modalMessage, alertModalMessage] = useState<string | null>(null);

  const titleRef = useRef<HTMLDivElement | null>(null);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  const contextHeadersRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const formButtonRef = useRef<HTMLDivElement | null>(null);
  const formWrapperRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollRef = useRef(false);
  const selectedRowRef  = useRef<HTMLTableRowElement | null>(null);
 
  const isOpenedFromList = (): boolean => {
    if (props.direct_form) return true;
    return (currentPkey ?? Global.FormPkeyState.NONE) === Global.FormPkeyState.NONE
  };
  // spread-list基本
  const [data, setData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [columns, setColumns] = useState<ColumnMeta[]>([]);
  // const [loading, setLoading] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(false);  // 条件・メタ情報
  const [loadingData, setLoadingData] = useState(false);  // データ本体
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // 顔画像の加工(face_preview)処理中セル。該当セルに「処理中…」プレビューを表示するためだけに使う。
  // （無加工画像の混入防止は handleFormFaceImageSelect が加工完了まで保存値を null にすることで担保）
  const [faceProcessingCells, setFaceProcessingCells] = useState<Set<string>>(new Set());
  const [userAllow, setUserAllow] = useState<Global.MetaUserAllow>(Global.MetaUserAllow.NONE);
  const visibleListColumns = columns.filter(
    c => !c.list_hidden && (c.length ?? 1) > 0 && c.type !== "label"
  );
  const visibleBulkColumns = columns.filter(
    c => !c.list_hidden && (c.length ?? 1) > 0 && c.type !== "link" && c.type !== "label" && !c.readonly
  );
  const requiredBulkColumns = visibleBulkColumns.filter(c => c.required);
  const listActionLeftPad = 5;
  const listFixedLeftPad = 5;
  const listFixedLeftBase = Global.getColumnWidth(1.2) + Global.getColumnWidth(4.0) + listActionLeftPad;
  const listFixedLeftMap = React.useMemo(() => {
    const map = new Map<string, { left: number; headerZIndex: number; bodyZIndex: number }>();
    let left = listFixedLeftBase;
    let fixedIndex = 0;

    visibleListColumns.forEach((col, index) => {
      if (!col.fixed_left) return;
      map.set(getListColumnKey(col, index), {
        left,
        headerZIndex: 5 + fixedIndex,
        bodyZIndex: 3 + fixedIndex,
      });
      left += Global.getColumnWidth(col.length ?? 0) + listFixedLeftPad;
      fixedIndex += 1;
    });

    return map;
  }, [visibleListColumns, listFixedLeftBase]);
  const visibleFormColumns = columns.filter(c => {
    if (c.form_hidden) return false;
    if (c.type === 'link') return false;
    const size = c.cols ?? c.length ?? 1;
    return size > 0;
  });
  // const visibleStepColumns = columns.filter(c => {
  //   if (c.type === 'link') return false;
  //   const size = c.cols ?? c.length ?? 1;
  //   return size > 0;
  // });
  // ライン編集専用の保持情報
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editBuffer, setEditBuffer] = useState<any>({});
  const [primaryKey, setPrimaryKey] = useState<string | Global.FormPkeyState.NONE>(Global.FormPkeyState.NONE);
  // const [meta, setMeta] = useState<any>(null);
  const [meta, setMeta] = useState<any | null | undefined>(undefined);
  const [uiFilters, setUiFilters] = useState<{ [key: string]: any }>(props.ui_filters || {});
  const [searchBuffer, setSearchBuffer] = useState<{ [key: string]: any }>(props.ui_filters || {});
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<number, Record<string, any>>>({});
  const filterable = (meta?.columns ?? []).filter((c: any) => c.ui_filter && !(props.base_filters && props.base_filters.hasOwnProperty(c.celldata)));
  const encodeOptionValue = (value: any) => {
    if (value === null || value === undefined) return "null";
    return Array.isArray(value) ? JSON.stringify(value) : String(value);
  };
  const resolveOptionValue = (options: any[] | undefined, encoded: string) => {
    const found = (options ?? []).find((o: any) => encodeOptionValue(o.value) === encoded);
    return found ? found.value : encoded;
  };
  // const [viewMode, setViewMode] = useState<"list" | "form">("list");
  // 子SpreadTable表示
  const [childSpread, setChildSpread] = useState<string | null>(null);
  const [childFilter, setChildFilter] = useState<any>(null);
  const [childRefKey, setChildRefKey] = useState<string | null>(null);
  // 一括追加パネル
  const [showBulkPanel, setShowBulkPanel] = useState<boolean>(false);
  const [bulkRows, setBulkRows] = useState<Record<string, any>[]>([]);
  const [bulkRowErrors, setBulkRowErrors] = useState<Record<number, Record<string, string>>>({});
  const [bulkSaving, setBulkSaving] = useState<boolean>(false);

  // spread-form入力関係
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [isNewFormMode, setIsNewFormMode] = useState<boolean>(false);
  const faseFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const cameraInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // ★ 追加：フォームが「新規モード」かどうか
  // フォーム編集状態（true＝編集中、false＝閲覧専用）
  const [isFormEditing, setIsFormEditing] = useState<boolean>(false);
  const setFormEditing = (value: boolean) => {
    // audioRefs.current = {};       // 常に初期化
    // setAudioUrls({});             // 常に初期化
    setIsFormEditing(value);      // 状態を指定通りに変更
  };

  // フォーム表示に切り替える専用のキー保持
  const [formPkey, setFormPkey] = useState<string>( props.form_pkey ?? Global.FormPkeyState.NONE );

  // // 参照モードは基本ALLだが、「新規フォーム」は最初からSTEP扱いにする
  // const effectiveFormCursorType =
  //   (!isFormEditing && formPkey !== Global.FormPkeyState.NEW)
  //     ? Global.FormCursorType.ALL
  //     : formCursorType;

  const shouldShowConfirm =
    formCursorType === Global.FormCursorType.ALL_CONFIRM &&
    formPkey !== Global.FormPkeyState.NEW;
  const isStepFormCursor =
    formCursorType === Global.FormCursorType.STEP_FORWARD_ONLY ||
    formCursorType === Global.FormCursorType.STEP_FREE;
  
  const effectiveFormCursorType =
    shouldShowConfirm
      ? Global.FormCursorType.ALL_CONFIRM
      : (
          formCursorType === Global.FormCursorType.ALL_CONFIRM
            ? Global.FormCursorType.ALL_CONFIRM_SKIP
            : formCursorType
        );

      
  // 追加：再検索を制御するフラグ
  const skipNextFetchRef = useRef(false);
  // 新規保存後のuseEffect内resstartFormをスキップするフラグ
  const skipResstartFormRef = useRef(false);

  const isConditionMatched = (condition: ColumnCondition | undefined, values: Record<string, any>): boolean => {
    if (!condition?.celldata) return false;
    const current = values[condition.celldata];
    const currentText = String(current ?? "");
    if (Array.isArray(condition.values)) {
      if (condition.values.length === 0) return currentText === "";
      return condition.values.some((v) => currentText === String(v ?? ""));
    }
    if (Array.isArray(condition.not_values)) {
      if (condition.not_values.length === 0) return currentText !== "";
      return !condition.not_values.some((v) => currentText === String(v ?? ""));
    }
    return false;
  };

  const applyReadonlyClear = (values: Record<string, any>): Record<string, any> => {
    const next = { ...values };
    columns.forEach((col) => {
      if (col.readonly_when?.clear && isConditionMatched(col.readonly_when, next)) {
        next[col.celldata] = "";
      }
    });
    return next;
  };

  const isReadOnly = (col: ColumnMeta, values: Record<string, any> = editValues): boolean => {
    if (col.readonly) return true;
    if (isConditionMatched(col.readonly_when, values)) return true;
    if ((col as any).base_filter_editable) return false;
    const baseFilters = resolvedProps?.base_filters;
    if (baseFilters && baseFilters.hasOwnProperty(col.celldata)) {
      return true;
    }
    return false;
  };

  const formatInitValue = (value: any, col: ColumnMeta): any => {
    if (value === null || value === undefined || value === "") return value;
    if (col.type === "datetime" || col.type === "sdatetime") {
      const text = String(value).trim();
      const datePart = text.split(/[ T]/)[0].replace(/-/g, "/");
      return `${datePart} 00:00:00`;
    }
    if (col.type === "date") {
      return String(value).trim().split(/[ T]/)[0].replace(/-/g, "/");
    }
    return value;
  };

  const isPlaceholderToken = (value: any): boolean => {
    if (typeof value !== "string") return false;
    return /^\{[^{}]+\}$/.test(value.trim());
  };

  const isUnresolvedAutoValue = (value: any, col: ColumnMeta): boolean => {
    if (!isPlaceholderToken(value)) return false;
    const text = String(value).trim();
    return [col.defvalue, col.updvalue].some(v => String(v ?? "").trim() === text);
  };

  const getDisplayValue = (col: ColumnMeta, value: any): any => {
    return isUnresolvedAutoValue(value, col) ? "" : value;
  };

  const buildNewFormInitialValues = (cols: ColumnMeta[] = []): Record<string, any> => {
    const initial: Record<string, any> = { ...(props.base_filters || {}) };
    cols.forEach((col) => {
      const source = col.initvalue;
      if (!source || !col.celldata) return;
      if (initial[col.celldata] !== null && initial[col.celldata] !== undefined && initial[col.celldata] !== "") return;
      const sourceValue = initial[source];
      if (sourceValue === null || sourceValue === undefined || sourceValue === "") return;
      initial[col.celldata] = formatInitValue(sourceValue, col);
    });
    return initial;
  };

  const isRequired = (col: ColumnMeta, values: Record<string, any> = editValues): boolean => {
    return Boolean(col.required || isConditionMatched(col.required_when, values));
  };

  const isHidden = (col: ColumnMeta, values: Record<string, any>): boolean => {
    return isConditionMatched(col.hidden_when, values);
  };
  

  function applyUIDToColumns(cols: ColumnMeta[]): ColumnMeta[] {
    return cols.map((c, idx) => ({
      ...c,
      temp_uid: c.temp_uid ?? Global.createClientUuid(),
    }));
  }
  function setColumnsWithUID(apiCols: ColumnMeta[]) {
    const colsWithUID = applyUIDToColumns(apiCols);
    setColumns(colsWithUID);
  }
 

  // STEPフォームの「表示対象カラム」一覧
  const formDisplayColumns = React.useMemo(
    () =>
      columns.filter(col => {
        // link は常に除外
        if (col.type === "link") return false;

        // label は特に特別視せず、通常通り判定する。

        // // label は無条件に表示（STEPの一部として扱う）
        // if (col.type === "label") return true;

        // サイズ 0 は表示しない
        const size = col.cols ?? col.length ?? 1;
        if (size <= 0) return false;

        // readonly は入力対象外（STEPでは飛ばす）
        if (isReadOnly(col)) return false;

        return true;
      }),
    [columns, resolvedProps?.base_filters, editValues]
  );


  const activeFormColumns = React.useMemo(() => {
    // 非編集（確認画面など）は全表示
    if (!isFormEditing) {
      return visibleFormColumns;
    }

    // STEP系
    if (
      formCursorType === Global.FormCursorType.STEP_FORWARD_ONLY ||
      formCursorType === Global.FormCursorType.STEP_FREE
    ) {
      const active = formDisplayColumns[curDispColIdx];
      if (!active) return [];

      // 同じ colname のみ表示
      const sameCols = visibleFormColumns.filter(
        // c => c.colname === active.colname
        c => c.temp_uid === active.temp_uid
      );

      // ★ label 重複除去（colname基準）
      const seen = new Set<string>();
      return sameCols.filter(c => {
        if (c.type !== "label") return true;
        if (seen.has(c.colname)) return false;
        seen.add(c.colname);
        return true;
      });
    }

    // ALL_DIRECT
    return visibleFormColumns;

  }, [
    isFormEditing,
    formCursorType,
    visibleFormColumns,
    formDisplayColumns,
    curDispColIdx
  ]);

  // const inlineGroups = groupInlineColumns(visibleFormColumns);
  // 既存の inlineGroups を差し替え
  const inlineGroups = React.useMemo(
    () => groupInlineColumns(activeFormColumns),
    [activeFormColumns]
  );

  const handleNext = () => {
    if (formDisplayColumns.length === 0) return;

    setCurDispColIdx(prev => {
      const now = Date.now();

      // いま表示している列の終了時刻
      const prevCol = formDisplayColumns[prev];
      if (prevCol) {
        prevCol.temp_end_at = now;
      }

      // 次へ
      const next = Math.min(prev + 1, formDisplayColumns.length - 1);

      // 次に表示される列の開始時刻（初回だけ）
      const nextCol = formDisplayColumns[next];
      if (nextCol && !nextCol.temp_start_at) {
        nextCol.temp_start_at = now;
      }

      return next;
    });
  };

  const handlePrev = () => {
    if (formDisplayColumns.length === 0) return;

    setCurDispColIdx(prev => {
      const now = Date.now();

      const prevCol = formDisplayColumns[prev];
      if (prevCol) {
        prevCol.temp_end_at = now;
      }

      const next = Math.max(prev - 1, 0);

      const nextCol = formDisplayColumns[next];
      if (nextCol && !nextCol.temp_start_at) {
        nextCol.temp_start_at = now;
      }

      return next;
    });
  };

  // 便利フラグ
  const isFirstStep = curDispColIdx <= 0 || formDisplayColumns.length === 0;
  const isLastStep  = formDisplayColumns.length === 0 || curDispColIdx >= formDisplayColumns.length - 1; 

  // 共通保持情報
  const [originalFormRow, setOriginalFormRow] = useState<Record<string, any>>({});

  const hasChanges = React.useMemo(() => {
    if (formPkey === Global.FormPkeyState.NEW) return true;
    if (!meta?.columns) return false;
    return meta.columns
      .filter((c: ColumnMeta) => c.type !== "link" && c.celldata)
      .some((c: ColumnMeta) =>
        String(editValues[c.celldata] ?? "") !== String(originalFormRow[c.celldata] ?? "")
      );
  }, [editValues, originalFormRow, formPkey, meta]);

  // formPkeyの内容によって表示するモードを切り分けする（editingRowは判定に利用しない）
  const isFormDisplay = () => formPkey !== Global.FormPkeyState.NONE
  const isCSVOutput = () => outputType === Global.OutputType.CSV
  const buildAgentSelectedRecord = () => {
    if (!isFormDisplay() || formPkey === Global.FormPkeyState.NEW) return null;
    const pkeyName = meta?.pkey ?? primaryKey ?? null;
    const selectedValues = Object.fromEntries(
      (meta?.columns ?? [])
        .filter((col: ColumnMeta) =>
          col.celldata &&
          col.type !== "link" &&
          col.type !== "label" &&
          !col.form_hidden &&
          (col.length ?? col.cols ?? 1) > 0
        )
        .slice(0, 30)
        .map((col: ColumnMeta) => [
          col.display || col.celldata,
          editValues[col.celldata] ?? originalFormRow[col.celldata] ?? null
        ])
    );
    return {
      mode: "form" as const,
      source: selectedRow !== null ? "selected_from_list" as const : "direct_form" as const,
      pkey_name: pkeyName,
      pkey_value: formPkey,
      row_index: selectedRow !== null ? selectedRow + 1 : null,
      visible_values: selectedValues,
    };
  };
  useEffect(() => {
    setAgentViewContext({
      source: "spreadtable",
      spread_id: String(props.spread ?? ""),
      display: meta?.display ?? null,
      mode: isFormDisplay() ? "form" : "list",
      pkey: formPkey && formPkey !== Global.FormPkeyState.NONE ? String(formPkey) : null,
      form_pkey: formPkey ? String(formPkey) : null,
      selected_record: buildAgentSelectedRecord(),
      base_filters: props.base_filters ?? null,
      ui_filters: uiFilters ?? null,
      current_path: typeof window === "undefined" ? null : window.location.pathname,
    }, agentViewOwnerIdRef.current);
    return () => clearAgentViewContext(agentViewOwnerIdRef.current);
  }, [
    props.spread,
    meta?.display,
    meta?.pkey,
    JSON.stringify(meta?.columns ?? []),
    formPkey,
    selectedRow,
    JSON.stringify(editValues ?? {}),
    JSON.stringify(originalFormRow ?? {}),
    JSON.stringify(props.base_filters ?? {}),
    JSON.stringify(uiFilters ?? {}),
    setAgentViewContext,
    clearAgentViewContext,
  ]);
  // ② コンポーネント内の state にこれを追加（共通エラー状態を保持）
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ▼ page-body の高さを取得する共通関数
  const getPageBodyHeight = (): number => {
    const body = document.querySelector(".page-body") as HTMLElement | null;
    return body?.clientHeight ?? 0;
  };

  // ▼ 自分のタイトル／フィルタ／ラッパー／オーバーレイの高さを更新
  const updateSize = () => {
    const bodyHeight = getPageBodyHeight();
    if (!bodyHeight) return;
    const titleH   = titleRef.current?.offsetHeight   ?? 0;

    // titleRef はタイトル・固定条件・コンテキストヘッダー・任意条件を含む。

    // いままでコメントしていた数式をそのまま採用
    // const wrapperH = bodyHeight - (titleH + filtersH + 24);
    // const overlayH = bodyHeight - (titleH + filtersH + 60);
    if (wrapperRef.current) {
      // wrapperRef.current.style.height = `${wrapperH+60+24}px`;
      // wrapperRef.current.style.height = `${wrapperH+48}px`;
      wrapperRef.current.style.height = `${Math.max(120, bodyHeight - titleH - 24)}px`;
    }
    if (formWrapperRef.current) {
      const formButtonH = formButtonRef.current?.offsetHeight ?? 0;
      formWrapperRef.current.style.maxHeight = `${Math.max(120, bodyHeight - (titleH + formButtonH + 24))}px`;
    }
    if (overlayRef.current) {
      // overlayRef.current.style.height = `${overlayH+60+24}px`;
      overlayRef.current.style.height = `${bodyHeight}`;
    }
  };

  // useEffect(() => {
  //   if (typeof navigator !== "undefined") {
  //     setIsIOS(/iP(hone|od|ad)/.test(navigator.userAgent));
  //   }
  // }, []);

  // ▼ 初期計算＋リサイズ＋DOM変化で再計算
  useEffect(() => {

    // alert(`初回direct_form：${props.direct_form}`);
    // 初回
    updateSize();
    // ウィンドウリサイズ
    const handleResize = () => updateSize();
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    // page-body 内の DOM 変化（行追加など）にも追随する
    const body = document.querySelector(".page-body");
    let observer: MutationObserver | null = null;
    if (body) {
      observer = new MutationObserver(() => updateSize());
      observer.observe(body, { childList: true, subtree: true });
    }
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateSize());
      [body, titleRef.current, filtersRef.current, contextHeadersRef.current, formButtonRef.current]
        .filter((el): el is Element => Boolean(el))
        .forEach(el => resizeObserver?.observe(el));
    }
    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      observer?.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => updateSize());
  }, [formPkey, childSpread, childFilter]);


  useEffect(() => {

// alert(JSON.stringify(props.base_filters));
// alert(JSON.stringify(props.form_pkey));
    const resolveProps = async () => {
      const data = await resolveSpreadFilters(
        props.spread,
        props.base_filters,
        props.ui_filters,
        props.form_pkey ?? Global.FormPkeyState.NONE,
      );
      setResolvedProps({
        ...props,
        base_filters: data.base_filters,
        ui_filters: data.ui_filters,
        form_pkey: data.form_pkey ?? Global.FormPkeyState.NONE,
      });
    };
    resolveProps();
  }, [JSON.stringify(props)]);  // propsの変化を監視

  useEffect(() => {
    if (props.ui_filters) {
      setUiFilters(props.ui_filters);
      setSearchBuffer(props.ui_filters);
    }
  }, [JSON.stringify(props.ui_filters)]);
  
  useEffect(() => {
    // ▼ フォーム切替時は顔加工の処理中フラグを掃除（万一の残留オーバーレイ防止の保険）
    setFaceProcessingCells(new Set());
    if (formPkey === Global.FormPkeyState.NONE) {
      // ▼ リストモード
      setIsNewFormMode(false);  // ★ フォーム自体表示されてないので false
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false;
        return;
      }
      // ▼ 非リストの場合は再検索を抑制
      if (!isOpenedFromList()) {
        //alert('リスト検索を抑制した。')
        return;
      } 

      switch (outputType) {
        case Global.OutputType.SCREEN:
          // スクリーン表示：データのロード・表示
          fetchData();
          setSelectedRow(null);
          setEditValues({});
          break
        case Global.OutputType.CSV:
          // CSV出力：メタ情報のロード・表示
          fetchMeta();
          break
      }



    } else if (formPkey === Global.FormPkeyState.NEW) {
      // ▼ 新規作成モード
      setIsNewFormMode(true);   // ★ 新規フォーム
      resstartForm();
      // ① すでにメタ情報があるなら、それをそのまま使う（リストから来たパターン）
      if (meta && meta.columns && meta.columns.length > 0) {
        // setFormEditing(true);
        handleStartEdit();
        setColumnsWithUID(meta.columns);  // すでにあるカラムを再利用
        const initial = buildNewFormInitialValues(meta.columns || []);
        setEditValues(initial);
        setOriginalFormRow(initial);
        setPrimaryKey(meta.pkey ?? Global.FormPkeyState.NONE);
        setUserAllow((meta.user_allow as Global.MetaUserAllow) ?? Global.MetaUserAllow.NONE);
        setLoadingMeta(false);  // 念のためここでOFFにしておく
        return; // ★ API呼ばず終了
      }
      // ② メタがまだ無いときだけ、従来通り API で取得
      fetchMeta();
    } else {
      // ▼ 既存編集モード
      setIsNewFormMode(false);  // ★ 既存レコード編集
      if (skipResstartFormRef.current) {
        skipResstartFormRef.current = false;  // 新規保存後 → resstartForm スキップ
        setCurDispColIdx(0);                  // setFormEditing(true) はスキップ、インデックスだけリセット
      } else {
        resstartForm();
      }
      // ① まず「すでに読み込んでいるリストの中に対象行があるか」を見る
      if (
        primaryKey && 
        primaryKey !== Global.FormPkeyState.NONE &&
        data && data.length > 0
      ) {
        const existingRow = data.find(
          r => String(r[primaryKey]) === String(formPkey)
        );
        //alertModalMessage(JSON.stringify(meta));
        if (existingRow && meta) {
          // 🔹 リストから来たパターン：サーバー再読込なしでそのまま流用
          setEditValues(existingRow);
          setOriginalFormRow(existingRow);  // 差分比較用も同期
          // meta / columns / userAllow / primaryKey は fetch 済みのものをそのまま使う
          return; // ← ここで終了。fetchRow は呼ばない
        }
      }
      // ② ここまで来たら「リストに元データが無い」＝PKEY直指定などのパターン
      fetchRow();
    }
  }, [
    formPkey,        // モード切り替えのトリガー
    props.spread,    // スプレッドが変わった時だけ
  ]);

  useEffect(() => {
    if (formPkey === Global.FormPkeyState.NONE && pendingScrollRef.current) {
      pendingScrollRef.current = false;
      requestAnimationFrame(() => {
        selectedRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }, [formPkey]);

  const fetchMeta = async () => {
    try {
      setLoadingMeta(true);
      const res = await axios.post<SpreadMetaResponse>(`${Global.getApiBaseUrl()}/spread/get_spreadmeta`, {
        // spread: String(props.spread),
        // 社員GUID: employeeGUID,

        base_token_interface: baseTokenInterface(),
        body_data: {
          spread_id: String(props.spread),
        }
 
      });
 
      // setFormEditing(true);
      handleStartEdit();
      setMeta(res.data.meta);
      setColumnsWithUID(res.data.meta.columns || []);
      const initial = buildNewFormInitialValues(res.data.meta.columns || []);
      setEditValues(initial);
      setOriginalFormRow(initial);
      setPrimaryKey(res.data.meta.pkey ?? Global.FormPkeyState.NONE);
      setUserAllow(res.data.meta.user_allow as Global.MetaUserAllow ?? Global.MetaUserAllow.NONE);
    } catch (err) {
      console.error("新規フォーム初期化失敗", err);
      setErrorMessage(`新規フォーム初期化失敗しました。||${(err as Error)?.message || JSON.stringify(err)}`);
    } finally {
      setLoadingMeta(false);
    }
  };

  const applySpreadSheetData = (
    m: SpreadSheetResponse["meta"] | null,
    rows: any[]
  ) => {
    const safeRows = Array.isArray(rows) ? rows.filter((row) => row && typeof row === "object") : [];
    setMeta(m ?? null);
    setPrimaryKey(m?.pkey ?? Global.FormPkeyState.NONE);
    setUserAllow((m?.user_allow as Global.MetaUserAllow) ?? Global.MetaUserAllow.NONE);
    if (m?.columns && Array.isArray(m.columns)) {
      setColumnsWithUID(m.columns);
    }
    setData(safeRows);
    setOriginalData(safeRows);
  };

  const resetListScroll = () => {
    if (!wrapperRef.current) return;
    wrapperRef.current.scrollTop = 0;
    wrapperRef.current.scrollLeft = 0;
  };

  const getSpreadSheetData = async (
    filters: { [key: string]: any } = uiFilters
  ): Promise<FetchSpreadSheetResult> => {
    const res = await axios.post<SpreadSheetResponse>(`${Global.getApiBaseUrl()}/spread/get_spreadsheet`, {
      base_token_interface: baseTokenInterface(),
      body_data: {
        spread_id: String(props.spread),
        ui_filters: filters,
        base_filters: props.base_filters,
        sort: props.sort,
      }
    });


 
    // alert(JSON.stringify(res.data.meta));

    return {
      meta: res.data?.meta ?? null,
      rows: res.data?.rows ?? []
    };
  };

  const fetchRow = async (): Promise<boolean> => {
    try {
      setLoadingMeta(true);
      const res = await axios.post<SpreadRowResponse>(`${Global.getApiBaseUrl()}/spread/get_spreadrow`, {

        base_token_interface: baseTokenInterface(),
        body_data: {
          spread_id: String(props.spread),
          ui_filters: uiFilters,
          base_filters: props.base_filters,
          key_value: formPkey,
        }


      });
      setMeta(res.data.meta);
      setColumnsWithUID(res.data.meta.columns || []);
      setEditValues(res.data.row);
      setOriginalFormRow(res.data.row);
      setPrimaryKey(res.data.meta.pkey);
      setUserAllow(res.data.meta.user_allow as Global.MetaUserAllow ?? Global.MetaUserAllow.NONE);
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 400) {
        // 読み取り専用スプレッド or pkey未定義のため行取得できない → リストへ戻す
        setFormPkey(Global.FormPkeyState.NONE);
      } else {
        console.error("フォーム初期化失敗", err);
        setErrorMessage(`フォーム初期化失敗しました。||${(err as Error)?.message || JSON.stringify(err)}`);
      }
      return false;
    } finally {
      setLoadingMeta(false);
    }
  };

  // const fetchData = async (filters = uiFilters) => {
  //   try {
  //     // 🔹 ロード開始
  //     setLoadingData(true);
  //     setData([]); // 既存データを一旦クリア（残像防止）
  //     setErrorMessage(null); // 前回のエラーもクリア

  //     const res = await axios.post<SpreadSheetResponse>(`${Global.getApiBaseUrl()}/spread/get_spreadsheet`, {

  //       base_interface: {
  //         user_guid: employeeGUID,
  //         user_domain: employeeDomain,
  //         ip_address: window.location.hostname,
  //         user_agent: navigator.userAgent,
  //       },
  //       body_data: {
  //         spread_id: String(props.spread),
  //         ui_filters: filters,
  //         base_filters: props.base_filters,
  //         sort: props.sort,
  //       }


  //     });

  //     const m = res.data?.meta;
  //     const rows = res.data?.rows ?? [];

  //     // ① 先にメタを固定
  //     setMeta(m ?? null);
  //     setPrimaryKey(m?.pkey ?? Global.FormPkeyState.NONE);
  //     setUserAllow(m?.user_allow as Global.MetaUserAllow ?? Global.MetaUserAllow.NONE);
  //     if (m?.columns && Array.isArray(m.columns)) {
  //       setColumnsWithUID(m.columns);
  //     }

  //     // ② 後で rows
  //     setData(rows);
  //     setOriginalData(rows);

  //   } catch (err) {
  //     setErrorMessage(`スプレッド定義の取得に失敗しました||${(err as Error)?.message || JSON.stringify(err)}`);
  //     console.error("スプレッド取得エラー", err);
  //   } finally {
  //     // 🔹 ロード終了
  //     setLoadingData(false);
  //   }
  // };
  
  const fetchData = async (filters = uiFilters) => {
    console.debug("[SpreadTable fetchData]", {
      spread: props.spread,
      filters,
      formPkey,
      outputType,
      time: new Date().toISOString(),
      stack: new Error().stack,
    });
    try {
      setLoadingData(true);
      setData([]);
      setErrorMessage(null);

      const { meta, rows } = await getSpreadSheetData(filters);
      applySpreadSheetData(meta, rows);
      setUiFilters({ ...filters });
      requestAnimationFrame(resetListScroll);

    } catch (err) {
      setErrorMessage(`スプレッド定義の取得に失敗しました||${(err as Error)?.message || JSON.stringify(err)}`);
      console.error("スプレッド取得エラー", err);
    } finally {
      setLoadingData(false);
    }
  };

  const refreshDataSilently = async (filters = uiFilters) => {
    try {
      setErrorMessage(null);
      const { meta, rows } = await getSpreadSheetData(filters);
      applySpreadSheetData(meta, rows);
    } catch (err) {
      setErrorMessage(`スプレッド定義の取得に失敗しました||${(err as Error)?.message || JSON.stringify(err)}`);
      console.error("スプレッド再取得エラー", err);
    }
  };

  const exportSpreadSheetCsv = (
    m: SpreadSheetResponse["meta"] | null,
    originRows: any[]
  ) => {
    const exportColumns = (m?.columns ?? []).filter((c: ColumnMeta) => {
      if (c.type === "link") return false;
      if (c.type === "label") return false;
      if (c.list_hidden) return false;
      const size = c.cols ?? c.length ?? 1;
      return size > 0;
    });

    const headers = exportColumns.map(col => col.display || col.celldata);
    const rows = originRows.map((row: Record<string, any>) =>
      exportColumns.map(col => {
        const val = renderCell(col, row[col.celldata]);
        return `"${String(val ?? "").replace(/"/g, '""')}"`
      }).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\r\n");
    const now = new Date();
    const yyyymmddhhnn =
      `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}${String(now.getHours()).padStart(2,"0")}${String(now.getMinutes()).padStart(2,"0")}`;

    const filename = `${m?.display ?? props.spread}_${yyyymmddhhnn}.csv`;
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // const fetchDataForCSV = async (
  //   filters: { [key: string]: string } = uiFilters,
  //   reflectToScreen: boolean = false
  // ) => {
  //   try {
  //     setExportingCSV(true);
  //     setErrorMessage(null);

  //     const { meta, rows } = await getSpreadSheetData(filters);

  //     if (reflectToScreen) {
  //       applySpreadSheetData(meta, rows);
  //       setUiFilters({ ...filters });
  //     }

  //     exportSpreadSheetCsv(meta, rows);

  //   } catch (err) {
  //     setErrorMessage(`スプレッド定義の取得に失敗しました||${(err as Error)?.message || JSON.stringify(err)}`);
  //     console.error("スプレッド取得エラー", err);
  //   } finally {
  //     setExportingCSV(false);
  //   }
  // };
  const fetchDataForCSV = async (
    filters: { [key: string]: any } = uiFilters,
    reflectToScreen: boolean = false
  ) => {
    const useScreenLoading = reflectToScreen;

    try {
      if (useScreenLoading) {
        setLoadingData(true);
      } else {
        setExportingCSV(true);
      }

      setErrorMessage(null);

      const { meta, rows } = await getSpreadSheetData(filters);

      if (reflectToScreen) {
        applySpreadSheetData(meta, rows);
        setUiFilters({ ...filters });
      }

      exportSpreadSheetCsv(meta, rows);

    } catch (err) {
      setErrorMessage(`スプレッド定義の取得に失敗しました||${(err as Error)?.message || JSON.stringify(err)}`);
      console.error("スプレッド取得エラー", err);
    } finally {
      if (useScreenLoading) {
        setLoadingData(false);
      } else {
        setExportingCSV(false);
      }
    }
  };

  const handleExportCurrentScreenCsv = () => {
    if (!meta) return;
    exportSpreadSheetCsv(meta, data);
  };

  const validateUiFilterRequired = (filters: { [key: string]: any }): boolean => {
    const missing = filterable.filter((c: ColumnMeta) =>
      c.ui_filter_required && (filters[c.celldata] === undefined || filters[c.celldata] === null || String(filters[c.celldata]).trim() === "")
    );
    if (missing.length === 0) return true;

    setErrorMessage(`検索条件を指定してください||${missing.map((c: ColumnMeta) => c.display ?? c.celldata).join("、")}`);
    return false;
  };

  const handleSearchClick = async () => {
    if (!validateUiFilterRequired(searchBuffer)) return;
    cancelLineEditing();
    await fetchData(searchBuffer);
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter") return;
    if (e.nativeEvent.isComposing) return;
    if (outputType !== Global.OutputType.SCREEN) return;
    e.preventDefault();
    handleSearchClick();
  };

  const handleCsvExportClick = async (reflectToScreen: boolean = false) => {
    if (!validateUiFilterRequired(searchBuffer)) return;
    await fetchDataForCSV(searchBuffer, reflectToScreen);
  };
  
  // const fetchDataForCSV = async (filters: { [key: string]: string } = uiFilters) => {
  //   try {
  //     // setLoadingData(true);
  //     setExportingCSV(true);                       // ★ スピン開始
  //     setErrorMessage(null);
  //     const res = await axios.post<SpreadSheetResponse>(`${Global.getApiBaseUrl()}/spread/get_spreadsheet`, {
  //       base_interface: {
  //         user_guid: employeeGUID,
  //         user_domain: employeeDomain,
  //         ip_address: window.location.hostname,
  //         user_agent: navigator.userAgent,
  //       },
  //       body_data: {
  //         spread_id: String(props.spread),
  //         ui_filters: filters,
  //         base_filters: props.base_filters,
  //         sort: props.sort,
  //       }

  //     });
  //     const m = res.data?.meta;
  //     const originRows: any[] = res.data?.rows ?? [];
  //     setMeta(m ?? null);
  //     setPrimaryKey(m?.pkey ?? Global.FormPkeyState.NONE);
  //     setUserAllow((m?.user_allow as Global.MetaUserAllow) ?? Global.MetaUserAllow.NONE);
  //     if (m?.columns && Array.isArray(m.columns)) {
  //       setColumnsWithUID(m.columns);
  //     }
  //     const exportColumns = (m?.columns ?? []).filter(c => {
  //       if (c.type === "link") return false;
  //       if (c.type === "label") return false;
  //       const size = c.cols ?? c.length ?? 1;
  //       return size > 0;
  //     });
  //     const headers = exportColumns.map(col => col.display || col.celldata);
  //     const rows = originRows.map((row: Record<string, any>) =>
  //       exportColumns.map(col => {
  //         const val = renderCell(col, row[col.celldata]);
  //         return `"${String(val ?? "").replace(/"/g, '""')}"`
  //       }).join(",")
  //     );
  //     const csvContent = [headers.join(","), ...rows].join("\r\n");
  //     const now = new Date();
  //     const yyyymmddhhnn = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}${String(now.getHours()).padStart(2,"0")}${String(now.getMinutes()).padStart(2,"0")}`;
  //     const filename = `${(m as any)?.display ?? props.spread}_${yyyymmddhhnn}.csv`;
  //     const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
  //     const blob = new Blob([bom, csvContent], { type: "text/csv;charset=utf-8;" });
  //     const url = URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = filename;
  //     a.click();
  //     URL.revokeObjectURL(url);
  //   } catch (err) {
  //     setErrorMessage(`スプレッド定義の取得に失敗しました||${(err as Error)?.message || JSON.stringify(err)}`);
  //     console.error("スプレッド取得エラー", err);
  //   } finally {
  //     // setLoadingData(false);
  //     setExportingCSV(false);
  //   }
  // };


  // フォーム表示時の共通初期化
  const resstartForm = async () => {
    // ✅ ステップモード時は最初の質問に戻す
    if (
      formCursorType === Global.FormCursorType.STEP_FORWARD_ONLY ||
      formCursorType === Global.FormCursorType.STEP_FREE
    ) {
      setCurDispColIdx(0);
      setFormEditing(true); // ← これが肝
    }    
  }

  // 親の対象行だけ再取得して差し替える
  const refreshParentRowByKey = async (key: string) => {
    if (!primaryKey) return;
    try {
      const res = await axios.post<SpreadRowResponse>(
        `${Global.getApiBaseUrl()}/spread/get_spreadrow`,
        {
          base_token_interface: baseTokenInterface(),
          body_data: {
            spread_id: String(props.spread),
            ui_filters: uiFilters,
            base_filters: props.base_filters,
            key_value: key,
          }
        }
      );
      if (!res.data.row || typeof res.data.row !== "object") return;
      setData(prev =>
        prev.map(r => (String(r[primaryKey]) === String(key) ? res.data.row : r))
      );
    } catch (err: any) {
      const status = err?.response?.status;
      if (status !== 404 && status !== 400) {
        console.error("行の再取得失敗", err);
      }
      // 404/400 は静かに無視（読み取り専用スプレッドでは行更新不要）
    }
  };

  const resolveSpreadFilters = async (
    spread: string,
    base_filters: Record<string, any> | undefined,
    ui_filters: Record<string, any> | undefined,
    form_pkey: string | Global.FormPkeyState.NONE,
  ): Promise<{
    base_filters: Record<string, any>,
    ui_filters: Record<string, any>,
    form_pkey: string | null
  }> => {
    const res = await axios.post<any>(
      `${Global.getApiBaseUrl()}/spread/resolve_filters`,
      {

        base_token_interface: baseTokenInterface(),
        body_data: {
          base_filters,
          ui_filters,
          form_pkey,
        }
      }
    );


    // res.data.form_cursor_type=props.form_cursor_type;
    return res.data;
  };

  const handleDetail = (e: React.MouseEvent, rowIndex: number) => {

    e.stopPropagation();  // ← まず tr のクリックイベントを止める
    setSelectedRow(rowIndex);  // ← ボタン押下でも選択色を付ける

    // 非編集状態の参照モードを初期値にする
    setFormEditing(false);

    const row = data[rowIndex]; // ✅
    if (!row) return;
    setFormPkey(row[primaryKey ?? "PKEY"]);

  };
  
  const handleEdit = (rowIndex: number) => {
    setEditingRow(rowIndex);
    setEditBuffer({ ...data[rowIndex] });
    setEdits(prev => {
      const { [rowIndex]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleCancel = () => {
    if (editingRow !== null) {
      setEdits(prev => {
        const { [editingRow]: _, ...rest } = prev;
        return rest;
      });
    }
    setEditingRow(null);
    setEditBuffer({});
  };

  const cancelLineEditing = () => {
    // 再検索時は行番号が別レコードを指すため、ライン編集の一時状態を破棄する。
    setEditingRow(null);
    setEditBuffer({});
    setEdits({});
  };

  const handleSave = async (rowIndex: number) => {
    if (!meta || !data?.[rowIndex]) return;
    const row = data[rowIndex];
    const pkey: string = meta.pkey!;

    const edited = edits[rowIndex] || {};
    const original = data[rowIndex];
    const merged = { ...original, ...edited }; // 現在値
    for (const col of columns) {
      if (!isRequired(col, merged)) continue;
      const val = merged[col.celldata];
      if (val === null || val === undefined || String(val).trim() === "") {
        alertModalMessage(`「${col.display}」を入力してください。`);
        return;
      }
    }
    const changed = getChangedValues(original, merged, pkey);  // ★統一利用
    if ((changed._changed as string[]).length === 0) return;

    try {
      const res = await axios.post<UpdateRowResponse>(`${Global.getApiBaseUrl()}/spread/update_row`, {

        base_token_interface: baseTokenInterface(),
        body_data: {
          spread_id: String(props.spread),
          row_data: changed,
          base_filters: props.base_filters,
          key_value: row[pkey],
        }


      });

      const updatedRow = res.data.row;
      if (!updatedRow || typeof updatedRow !== "object") {
        throw new Error("更新後の行データが取得できませんでした。");
      }

      setData(prev => {
        const copy = [...prev];
        copy[rowIndex] = updatedRow;
        return copy;
      });

      // 差分バッファクリア
      setEdits(prev => {
        const { [rowIndex]: _, ...rest } = prev;
        return rest;
      });
      notifyListChanged();

    } catch (err: any) {
      console.error("更新失敗", err.response?.data || err.message);
      setErrorMessage(`保存時に不明なエラーが発生しました。||${(err as Error)?.message || JSON.stringify(err)}`);
     } finally {
      setEditingRow(null);
      setEditBuffer({});
    }
  };

  
  // セル変更時に差分を蓄える
  const setCell = (rowIndex: number, colCellData: string, value: any) => {
    setEdits(prev => ({
      ...prev,
      [rowIndex]: { ...(prev[rowIndex] || {}), [colCellData]: value },
    }));
  };

  const setListCell = (rowIndex: number, row: any, colCellData: string, value: any) => {
    const current = { ...row, ...editBuffer };
    const next = applyReadonlyClear({ ...current, [colCellData]: value });
    const patch: Record<string, any> = {};
    columns.forEach((col) => {
      if (String(next[col.celldata] ?? "") !== String(current[col.celldata] ?? "")) {
        patch[col.celldata] = next[col.celldata];
      }
    });
    setEditBuffer((prev: any) => ({ ...prev, ...patch }));
    setEdits(prev => ({
      ...prev,
      [rowIndex]: { ...(prev[rowIndex] || {}), ...patch },
    }));
  };

  const handleDelete = async (rowIndex: number) => {
    try {
      const isConfirmed = window.confirm("この行を削除してもよろしいですか？");
      if (!isConfirmed) return;

      console.log("🗑️ 削除リクエスト:", data[rowIndex]);

      if (!primaryKey) {
        console.error("pkey が未設定");
        setErrorMessage("削除できません：主キーが設定されていません。");
        return;
      }

      setDeleting(true);

      await axios.post(`${Global.getApiBaseUrl()}/spread/delete_row`, {
        // spread: String(props.spread),
        // base_filters: props.base_filters,
        // key_value: data[rowIndex][primaryKey],
        // 社員GUID: employeeGUID,

        base_token_interface: baseTokenInterface(),
        body_data: {
          spread_id: String(props.spread),
          base_filters: props.base_filters,
          ui_filters: uiFilters,
          key_value: data[rowIndex][primaryKey],
        }


      });

      // カレント行を解除
      setSelectedRow(null);

      const updatedData = data.filter((_, i) => i !== rowIndex);
      setData(updatedData);
      notifyListChanged();
    } catch (err) {
      console.error("❌ 削除エラー:", err);
      setErrorMessage(`削除中に不明なエラーが発生しました。||${(err as Error)?.message || JSON.stringify(err)}`);
    } finally {
      setDeleting(false);
    }
  };

  // 列の合計幅を計算
  const totalColumnWidth = visibleListColumns.reduce(
    (sum, col) => sum + Global.getColumnWidth((col.length ?? 0)), 0
  );

  function renderCell(col:any, val:any) {
    if (isUnresolvedAutoValue(val, col)) return "";
    if (Array.isArray(col.options) && col.options.length > 0) {
      const map = new Map(col.options.map((o:any)=>[String(o.value), o.label]));
      const key = val === Global.FormPkeyState.NEW || val === Global.FormPkeyState.NONE ? Global.FormPkeyState.NEW : String(val);
      return map.get(key) ?? map.get(String(val)) ?? ""; // nullも拾えるように
    }
    return val ?? "";
  }

  // null → "-" に補正する関数
  const normalizeRow = (row: any) => {
    const newRow = { ...row };
    Object.keys(newRow).forEach((k) => {
      if (newRow[k] === null) {
        newRow[k] = "-";
      }
    });
    return newRow;
  };

  const getListAlignStyle = (align?: number | string): React.CSSProperties => {
    const normalized =
      align === 2 || align === "2" || String(align).toLowerCase() === "center"
        ? "center"
        : align === 3 || align === "3" || String(align).toLowerCase() === "right"
          ? "right"
          : "left";
    return { textAlign: normalized as React.CSSProperties["textAlign"] };
  };

  // 固定条件のOPTION指定時にラベルを表示する関数
  const renderFilterValue = (colCellData: string, value: any) => {
    const colMeta = columns.find(c => c.celldata === colCellData);

    // console.log(`colCellData: ${colCellData}`);
    // console.log(`columns: ${JSON.stringify(columns)}`);
    // console.log(`colMeta: ${JSON.stringify(colMeta)}`);


    if (colMeta?.options) {
      const opt = colMeta.options.find(o => String(o.value) === String(value));
      return opt ? opt.label : value;
    }
    return value;
  };

  //  共通判定 ここから ////////////////////////////////////////////////////////////////////////////////////////////////
  const notifyListChanged = () => {
    if (String(props.spread) === "AG11") {
      // 今はAG11だけの用途なのでspread_idを直接判定する。
      // 同種の連動が増えたら、YAMLのafter_save_event等へ移してSpreadTable仕様として扱う。
      window.dispatchEvent(new Event("agentlist-changed"));
    }
  };

  // const isUserAllowRead = (allow?: Global.MetaUserAllow): boolean => {
  //   return allow === Global.MetaUserAllow.R || allow === Global.MetaUserAllow.RW;
  // };

  const isUserAllowReadAndWrite = (allow?: Global.MetaUserAllow): boolean => {
    return allow === Global.MetaUserAllow.RW;
  };


  // const isReadOnly = (col: ColumnMeta): boolean => {
  //   // ① YAMLで readonly:true の場合
  //   if (col.readonly) return true;
  //   // ② ベースフィルタ（base_filters）で固定されてる場合
  //   if (props.base_filters && props.base_filters.hasOwnProperty(col.celldata)) {
  //     return true;
  //   }
  //   return false;
  // };  



  //  共通判定 ここまで ////////////////////////////////////////////////////////////////////////////////////////////////

  // 一括追加 ここから ////////////////////////////////////////////////////////////////////////////////////////////////
  const validateBulkRows = (): boolean => {
    const errors: Record<number, Record<string, string>> = {};

    bulkRows.forEach((row, rowIdx) => {
      const rowErrors: Record<string, string> = {};

      if (requiredBulkColumns.length > 0) {
        // required 列のみチェック
        requiredBulkColumns.forEach((col) => {
          const val = row[col.celldata];
          const isEmpty = val === null || val === undefined || String(val).trim() === "";
          if (isEmpty) {
            const hasOptions = Array.isArray(col.options) && col.options.length > 0;
            rowErrors[col.celldata] = hasOptions
              ? `「${col.display}」を選択してください`
              : `「${col.display}」を入力してください`;
          }
        });
      } else if (visibleBulkColumns.length > 0) {
        // required 列がゼロ・表示列あり → 完全空行のみ弾く（全列に個別エラーをセット）
        const allEmpty = visibleBulkColumns.every((col) => {
          const val = row[col.celldata];
          return val === null || val === undefined || String(val).trim() === "";
        });
        if (allEmpty) {
          visibleBulkColumns.forEach((col) => {
            const hasOptions = Array.isArray(col.options) && col.options.length > 0;
            rowErrors[col.celldata] = hasOptions
              ? `「${col.display}」を選択してください`
              : `「${col.display}」を入力してください`;
          });
        }
      }

      if (Object.keys(rowErrors).length > 0) errors[rowIdx] = rowErrors;
    });
    setBulkRowErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBulkSave = async () => {
    if (!validateBulkRows()) return;
    setBulkSaving(true);
    try {
      await axios.post(`${Global.getApiBaseUrl()}/spread/bulk_insert_rows`, {
        base_token_interface: baseTokenInterface(),
        spread_id: props.spread,
        bulk_rows: bulkRows,
        base_filters: props.base_filters,
      });
      setShowBulkPanel(false);
      setBulkRows([]);
      setBulkRowErrors({});
      fetchData(uiFilters);
      notifyListChanged();
    } catch (err) {
      const detail = (err as any)?.response?.data?.detail;
      setErrorMessage(`一括追加に失敗しました。||${detail || (err as Error)?.message || JSON.stringify(err)}`);
    } finally {
      setBulkSaving(false);
    }
  };
  // 一括追加 ここまで ////////////////////////////////////////////////////////////////////////////////////////////////

  // フォーム入力関連 ここから////////////////////////////////////////////////////////////////////////////////////////////////
  const handleFormSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!meta) return;
    if (isSaving) return;              // 🔥 連続クリックをブロック
    setIsSaving(true);
    // 🔍 必須項目Validation
    const requiredCols = (meta.columns ?? []).filter((c: ColumnMeta) => isRequired(c, editValues));
    for (const col of requiredCols) {
      const val = editValues[col.celldata];
      // null, 空文字, undefined のチェック
      if (val === null || val === undefined || String(val).trim() === "") {
        const isSelect = Array.isArray(col.options) && col.options.length > 0;
        const message = isSelect
          ? `「${col.display}」を選択してください。`
          : `「${col.display}」を入力してください。`;
        if (val === null || val === undefined || String(val).trim() === "") {
          alertModalMessage(message);
          setIsSaving(false);             // 🔥 復帰
          return;
        }
      }
    }
    // 書き込み事項
    try {
      stampLastStepEnd();
      if (formPkey === Global.FormPkeyState.NEW) {
        // 🔹 新規作成フロー
        const res = await axios.post<UpdateRowResponse>(`${Global.getApiBaseUrl()}/spread/insert_row`, {
          base_token_interface: baseTokenInterface(),
          body_data: {
            spread_id: String(props.spread),
            base_filters: props.base_filters,
            row_data: editValues,
          }

        });

        const newKey = res.data.key_value;
        console.log("✅ 新規作成成功:", newKey);

        // 新しく挿入した行を state に追加
        setData(prev => [...prev, res.data.row]);
        skipResstartFormRef.current = true;
        notifyListChanged();
        closeFormAfterSave();

      } else {
        const changed = getChangedValues(originalFormRow, editValues, primaryKey!);
        if ((changed._changed as string[]).length === 0) {
          console.log("変更なし");
          setFormEditing(false);  // 変更なしでも読取モードへ
        } else {
          const res = await axios.post<UpdateRowResponse>(`${Global.getApiBaseUrl()}/spread/update_row`, {
            base_token_interface: baseTokenInterface(),
            body_data: {
              spread_id: String(props.spread),
              row_data: changed,
              base_filters: props.base_filters,
              key_value: formPkey,
            }
          });
          const updatedRow = res.data.row;
          if (!updatedRow || typeof updatedRow !== "object") {
            throw new Error("更新後の行データが取得できませんでした。");
          }
          setData(prev => prev.map(row =>
            row && row[primaryKey!] === formPkey ? updatedRow : row
          ));
          notifyListChanged();
          closeFormAfterSave();
        }
      }
    } catch (err) {
      console.error("保存エラー:", err);
      setErrorMessage(`保存時に不明なエラーが発生しました。||${(err as Error)?.message || JSON.stringify(err)}`);
    } finally {
      setIsSaving(false);             // 🔥 復帰
    }
  };
  
  // 共通：フォームを閉じる処理
  const closeFormOnly = () => {

// console.log(`isOpenedFromList: ${Boolean(isOpenedFromList)}`);
// console.log(`props.direct_form: ${props.direct_form}`);
// console.log(`props.form_pkey: ${props.form_pkey}`);

    if (isOpenedFromList()){
      // selectedRow はそのまま保持（useEffect でスクロール位置復元に使用）
      if (props.direct_form) {
        // ダイレクト呼び出しの為、自スプレッドを閉じる
// console.log(`ここにきてる`);
        closeEntirePage();
      } else {
        // ✅ フォームから戻るときは再検索をスキップ
        pendingScrollRef.current = true;
        skipNextFetchRef.current = true;
        // 自スプレッド内のフォーム呼び出しの為スプレッドリストへ戻るだけ
        setFormPkey(Global.FormPkeyState.NONE);
      }
    } else {
      // キーが限定されている場合はここで完結する
      setFormPkey(Global.FormPkeyState.NONE);
      if (props.form_pkey == Global.FormPkeyState.NEW) {
        setDisplayMessage("登録しました。");
      } else {
        setDisplayMessage("変更を保存しました。");
      }
      return;
    }
  };

  const closeFormAfterSave = () => {
    if (isOpenedFromList()) {
      if (props.direct_form) {
        closeEntirePage();
      } else {
        pendingScrollRef.current = true;
        skipNextFetchRef.current = true;
        setFormPkey(Global.FormPkeyState.NONE);
      }
    } else {
      setFormPkey(Global.FormPkeyState.NONE);
      setDisplayMessage("正常に保存しました。");
    }
  };


  // 閉じて親に戻る
  const closeEntirePage = () => {
    props.onClose ? props.onClose() : setFormPkey(Global.FormPkeyState.NONE);
  }

  const handleEditValueChange = (colCellData: string, value: any) => {
    setEditValues(prev => applyReadonlyClear({ ...prev, [colCellData]: value }));
  };

  const parseDatePickerValue = (value: any): Date | null => {
    if (!value || ["", "0000-00-00"].includes(String(value))) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    const text = String(value).trim();
    const match = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::\d{1,2})?)?/);
    if (match) {
      const [, y, m, d, h = "0", mi = "0"] = match;
      const dt = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(mi));
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const dt = new Date(text);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const parseTimePickerValue = (value: any): Date | null => {
    if (!value || value instanceof Date && Number.isNaN(value.getTime())) return null;
    if (value instanceof Date) return value;

    const text = String(value).trim();
    const timeMatch = text.match(/(?:^|\s)(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
    if (!timeMatch) return null;

    const [, h, mi] = timeMatch;
    const dt = new Date();
    dt.setHours(Number(h), Number(mi), 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const formatDateValue = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateTimeValue = (date: Date): string => {
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    return `${formatDateValue(date)} ${hh}:${mi}`;
  };

  const formatTimeValue = (date: Date): string => {
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mi}`;
  };

  type InlineGroup = {
    cols: ColumnMeta[];
  };

  function groupInlineColumns(columns: ColumnMeta[]): InlineGroup[] {
    const groups: InlineGroup[] = [];
    let currentGroup: InlineGroup | null = null;

    // 🔥 まずここで link カラムを除外する
    const filtered = columns.filter(col => col.type !== "link");

    filtered.forEach((col) => {
      if (!col.inline) {
        // 新しい行を開始する
        currentGroup = { cols: [col] };
        groups.push(currentGroup);
      } else {
        // inline:true → 直前の行に連結
        if (!currentGroup) {
          // 先頭が inline:true の場合：独立行として扱う
          currentGroup = { cols: [] };
          groups.push(currentGroup);
        }
        currentGroup.cols.push(col);
      }
    });

    return groups;
  }


  // ▼ 新規/既存・デフォルト・null の扱いを統一する関数（全入力共通）
  const getFormValue = (col: ColumnMeta): string => {
    // const v = editValues[col.celldata];

    // // null/undefined → 新規と既存で挙動を分ける
    // if (v === null || v === undefined) {
    //   return isNewFormMode
    //     ? (col.defvalue ?? "")  // 新規 → defvalue or ""
    //     : "";                   // 既存 → ""（"null"は使わない）
    // }

    // // 通常は文字列化して返す
    // return String(v);

    const cell = col.celldata;
    const v = editValues[cell];

    let finalValue: string;

    // ▼ null/undefined → 新規と既存で分岐
    if (v === null || v === undefined) {
      const defaultValue = col.defvalue ?? "";
      finalValue = isNewFormMode && !isPlaceholderToken(defaultValue)
        ? (col.defvalue ?? "")   // 新規 → defvalue or ""
        : "";                    // 既存 → 空文字
    } else {
      finalValue = isUnresolvedAutoValue(v, col) ? "" : String(v);
    }

    // ▼ defvalue適用によって、画面表示値と内部ステートが変えた場合 → 自動的に内部値を同期する
    if (finalValue !== v && !isUnresolvedAutoValue(v, col)) {
      setEditValues(prev => ({
        ...prev,
        [cell]: finalValue
      }));
    }

    return finalValue;    

  };

  const handleStartEdit = () => {
    setFormEditing(true);

    // STEP モードのときだけログを取る
    if (
      formCursorType === Global.FormCursorType.STEP_FORWARD_ONLY ||
      formCursorType === Global.FormCursorType.STEP_FREE
    ) {
      const now = Date.now();
      const firstCol = formDisplayColumns[curDispColIdx] ?? formDisplayColumns[0];
      if (firstCol && !firstCol.temp_start_at) {
        firstCol.temp_start_at = now;
      }
    }
  };

  const handleReturnToDisplay = () => {
    setEditValues(originalFormRow);
    setFormEditing(false);
  };

  const stampLastStepEnd = () => {
    if (
      formCursorType === Global.FormCursorType.STEP_FORWARD_ONLY ||
      formCursorType === Global.FormCursorType.STEP_FREE
    ) {
      const now = Date.now();
      const col = formDisplayColumns[curDispColIdx];
      if (col) {
        col.temp_end_at = now;
      }
    }
  };

  // フォーム入力関連 ここまで////////////////////////////////////////////////////////////////////////////////////////////////

  // 共通関数 ここから///////////////////////////////////////////////////////////////////////////////////////////////////////

  function getChangedValues(
    original: Record<string, any>,
    current: Record<string, any>,
    pkey: string
  ): Record<string, any> {
    if (!meta?.columns) {
      // メタが無いときは今まで通り（必要ならここも絞ってOK）
      return { ...current, _changed: [] };
    }

    // 編集対象になる列だけ抽出（readonly / link を除外）
    const editableCols = meta.columns.filter((c: ColumnMeta) =>
      // !c.readonly &&
      c.type !== "link"
    );

    const changedKeys: string[] = [];
    const payload: Record<string, any> = {};

    // 主キーは必ず載せる
    payload[pkey] = current[pkey];

    for (const col of editableCols) {
      const k = col.celldata;
      if (!k || k === pkey) continue;

      const newVal = current[k];
      const oldVal = original[k];

      // 送信値としてコピー
      payload[k] = newVal;

      // 変更判定
      if (String(newVal ?? "") !== String(oldVal ?? "")) {
        changedKeys.push(k);
      }
    }

    payload._changed = changedKeys;
    return payload;
  }



  // 共通関数 ここまで///////////////////////////////////////////////////////////////////////////////////////////////////////


  const _normalizeFormPkey = (v: any) => {
    if (!v) return Global.FormPkeyState.NONE;
    if (v === Global.FormPkeyState.NONE) return Global.FormPkeyState.NONE;
    return String(v);
  };

  const openChild = (spread: string | null, ctx: Omit<ChildCtx, 'form_pkey'> & { form_pkey?: any }) => {
    if (!ctx || !ctx.base_filters) throw new Error('openChild(): ctx.base_filters is required');
    setChildSpread(spread);
    const normalized: ChildCtx = {
      base_filters: ctx.base_filters,
      ...(ctx.ui_filters ? { ui_filters: ctx.ui_filters } : {}),
      form_pkey: _normalizeFormPkey(ctx.form_pkey), // 必ず string（NONE含む）
      ...(ctx.__action ? { __action: ctx.__action } : {}),
    };
    setChildFilter(normalized);
    setChildRefKey(normalized.form_pkey === Global.FormPkeyState.NONE ? null : normalized.form_pkey);
  };


  const closeChild = async () => {
    setChildSpread(null);
    setChildFilter(null);
    setChildRefKey(null);
    requestAnimationFrame(() => {
      void refreshDataSilently(uiFilters);
    });
  };  

  // EXCEL エクスポート ここから ////////////////////////////////////////////////////////////////////////////////////////////////
  // ★ 追加：テンプレートフィルタ実数化＋POST→ダウンロード
  const handleExcelExport = async (row: any, linkData: ColumnLinkMeta, resolvedPkey:string) => {
    if (!linkData?.action?.action_template) return;

    try {
      setExportingExcel(true);                       // ★ スピン開始
      document.body.style.cursor = 'progress'; // ★ 全体カーソルをスピン風に

      // // 2) POST でバイナリ取得 → ブラウザ保存
      const fd = new FormData();
      fd.append("token", Global.getCurrentAuthToken(authToken) ?? "");
      fd.append("user_domain", employeeDomain ?? "");
      fd.append("ip_address", window.location.hostname);
      fd.append("user_agent", navigator.userAgent);
      fd.append("pkey", resolvedPkey);
      fd.append("action_type", "Excel-Builder");
      fd.append("action_template", linkData.action.action_template);
      // Excel Builder はバイナリ不要なので null → append しない
      const res = await axios.post<any>(
        `${Global.getApiBaseUrl()}/worker_push`,
        fd,
        { responseType: "blob" }
      );


      // ★ Content-Disposition ヘッダーから filename を抽出
      let filename = `${linkData.action.action_template}.xlsx`;
      const disposition = res.headers["content-disposition"] || res.headers["Content-Disposition"];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = decodeURIComponent(match[1]);
      }


      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename; // ← ★ここがサーバーのUUID付きになる
      a.click();
      window.URL.revokeObjectURL(url);



    } catch (err: any) {
      console.error('Excel出力エラー:', err);
      setErrorMessage(`Excel出力に失敗しました。||${err?.message ?? JSON.stringify(err)}`);
    } finally {
      setExportingExcel(false);                      // ★ スピン終了
      document.body.style.cursor = 'default';  // ★ カーソル戻す
    }
  };
  // EXCEL エクスポート ここまで ////////////////////////////////////////////////////////////////////////////////////////////////

  
  // 汎用関数：action_filters のラベルを行データの実値に置き換える
  const resolveActionFilters = ( row: Record<string, any>, linkData: ColumnLinkMeta): Record<string, any> => {
    const filters: Record<string, any> = {};
    for (const [key, value] of Object.entries(linkData.action?.action_filters ?? {})) {
      filters[key] =
        typeof value === "string" && value in row
          ? row[value]
          : value;
    }
    return filters;
  };

  // 汎用関数：action_pkey の列名を行データの実値に置き換える
  const resolveActionPkey = (row: Record<string, any>, linkData: ColumnLinkMeta): any => {
    const key = linkData.action?.action_pkey;
    if (!key) return null;
    // もし列名として存在するなら、その行の値を返す。存在しなければ key をそのまま返す。
    return (typeof key === "string" && key in row) ? row[key] : key;
  };

  const getEffectiveForeColor = (col: ColumnMeta, values: Record<string, any> = editValues): string => {
    if (col.forecolor_when?.forecolor && isConditionMatched(col.forecolor_when, values)) {
      return col.forecolor_when.forecolor;
    }
    return col.forecolor || "black";
  };

  const getForeColorStyle = (col: ColumnMeta, values: Record<string, any> = editValues): React.CSSProperties => (
    { color: getEffectiveForeColor(col, values) }
  );

  function changeLinkText(displayText: string, forecolor?: string): JSX.Element {
    if (displayText.startsWith("fasicon:")) {
      const iconData = displayText.slice("fasicon:".length);
      const [iconName] = iconData.split(",");

      return (<i className={`${iconName.trim()}`} style={{ padding:"0px 5px", color: forecolor || "black" }} />);
    }
    // 通常のテキスト表示
    return <span style={{ color: forecolor || "black" }}>{displayText}</span>;
  }

  function renderContextHeaders(): JSX.Element | null {
    const contextHeaders = (meta?.context_headers ?? []) as ContextHeader[];
    if (!Array.isArray(contextHeaders) || contextHeaders.length === 0) {
      return null;
    }
    const groupContextFields = (fields: ContextHeaderField[]): ContextHeaderField[][] => {
      const groups: ContextHeaderField[][] = [];
      let currentGroup: ContextHeaderField[] | null = null;
      fields.filter((field) => (field.length ?? 10) > 0).forEach((field) => {
        if (!field.inline) {
          currentGroup = [field];
          groups.push(currentGroup);
        } else {
          if (!currentGroup) {
            currentGroup = [];
            groups.push(currentGroup);
          }
          currentGroup.push(field);
        }
      });
      return groups;
    };
    return (
      <div className="context-headers-container" ref={contextHeadersRef}>
        {contextHeaders.map((header, headerIndex) => (
          <details className="context-header" key={`${header.title}-${headerIndex}`}>
            <summary className="context-header-title">
              <i className="fas fa-chevron-right context-header-awesome" />
              <span>{header.title}</span>
            </summary>
            <div className="context-header-fields">
              {groupContextFields(header.fields ?? []).map((group, groupIndex) => (
                <div className="context-header-row" key={`${header.title}-row-${groupIndex}`}>
                  {group.map((field, fieldIndex) => (
                    <div
                      className="context-header-field"
                      key={`${field.label}-${fieldIndex}`}
                      style={{
                        width: `${Global.getColumnWidth(field.length ?? 10)}px`
                      }}
                    >
                      <div className="context-header-label">🟩{field.label}</div>
                      <div className="context-header-value">{field.value ?? ""}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    );
  }

  // 一時バイナリアップロード
  const handleBinaryFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    cellName: string
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // ▼ API 送信用 FormData
      const fd = new FormData();
      fd.append("token", Global.getCurrentAuthToken(authToken) ?? "");
      fd.append("user_domain", employeeDomain ?? "");
      fd.append("ip_address", window.location.hostname);
      fd.append("user_agent", navigator.userAgent);
      // fd.append("spread_id", String(props.spread));
      fd.append("binary", file);

      // ▼ 即アップロード
      const res = await axios.post<any>(
        `${Global.getApiBaseUrl()}/spread/write_binary`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const bin_guid = res.data?.bin_guid;
      if (!bin_guid) throw new Error("bin_guid が取得できません");

      // ▼ GUID を保持
      setEditValues(prev => ({
        ...prev,
        [cellName]: bin_guid,
      }));

    } catch (err) {
      console.error("binary upload error", err);
      alertModalMessage("ファイルのアップロードに失敗しました。");
    }
  };

  // 一時フェイスアップロード
  const handleFormFaceImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    cellName: string
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (event) => {
      const originalDataUrl = event.target?.result as string;
      const originalBase64 = originalDataUrl.replace(/^data:.*;base64,/, "");

      // ▼ 列の type を取得
      const colMeta = columns.find(c => c.celldata === cellName);
      const type = colMeta?.type ?? "";

      // ▼ 非顔画像セルは従来どおり：プレビュー＝元画像／保存値＝元画像
      if (!type.startsWith("base64face")) {
        setEditValues(prev => ({ ...prev, [cellName]: originalDataUrl }));
        handleEditValueChange(cellName, originalBase64);
        return;
      }

      // ▼ 顔画像セル：保存値は加工完了まで null（処理中に保存/送信しても無加工画像を送らない）。
      //   editValues は表示と保存値を兼ねるため、プレビューは faceProcessingCells による
      //   「処理中…」表示で示す（元画像を editValues に入れると保存値が無加工になるため入れない）。
      handleEditValueChange(cellName, null);

      try {
        // ▼ 処理中フラグは try 内先頭で立て、必ず同じ try-finally で下ろす
        //   （add 後〜try 突入前の同期例外で finally を逃すと永久オーバーレイになるため）
        setFaceProcessingCells(prev => {
          const next = new Set(prev);
          next.add(cellName);
          return next;
        });

        // ▼ 加工（背景除去・顔切り抜き）
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/spread/face_preview`, {
          base_token_interface: baseTokenInterface(),
          body_data: {
            data_url: originalDataUrl,
            type: type,
          }

        });

        const processed = res.data?.processed_data_url;
        if (!processed) throw new Error("processed_data_url が無い");

        // ▼ プレビュー＋保存値を加工済みbase64（MIMEなし）で確定。
        //   handleEditValueChange が editValues も更新し、描画は data:image/webp;base64,${editValues}
        //   で組み立てるため、MIMEなしで揃える（MIMEつきを別途 setEditValues すると二重接頭辞になる）。
        handleEditValueChange(cellName, processed.replace(/^data:.*;base64,/, ""));

      } catch (err: any) {
        console.error("プレビュー顔処理エラー", err);

        // ▼ 失敗時（422含む）は保存値・プレビューをクリアして撮り直しを促す（エラーでなく行動指示）
        handleEditValueChange(cellName, null);
        setEditValues(prev => ({ ...prev, [cellName]: "" }));
        const detail =
          err?.response?.data?.detail ||
          "顔をうまく認識できませんでした。顔がはっきり写るように撮り直してください。";
        alertModalMessage(detail);

      } finally {
        setFaceProcessingCells(prev => {
          const next = new Set(prev);
          next.delete(cellName);
          return next;
        });
      }
    };

    reader.readAsDataURL(file);
  };

  // メッセージ表示
  if (displayMessage) {
    return (
        <div>{displayMessage}</div>
    );
  }


  // {true ? (
  //   <>
  //     {true ? (
  //       <>
  //       </>
  //     ) : null }
  //     {true && (
  //       <>
  //       </>
  //     )}
  //     {true && <></>}
  //   </>
  // ) : true ? (
  //   <>
  //   </>
  // ) : true ? (
  //   <>
  //   </>
  // ) : (
  //   <>
  //   </>
  // )}


  //  エラー返却 ここから ////////////////////////////////////////////////////////////////////////////////////////////////
  // if (!resolvedProps && !loadingMeta && !loadingData) return (
  //   <h2 style={{ color: "#888" }}>重大なエラーが発生しました。[{props.spread}]</h2>
  // );
  // if (!resolvedProps && !loadingMeta && !loadingData && userAllow === Global.MetaUserAllow.NONE && !errorMessage) return (
  //   <div>この帳票にはアクセスできません</div>
  // );

  // ▼ エラー返却（ロード中は絶対にここに来させない）


  // if (meta === null && loadingMeta === false) {
  //   return (
  //     <h2 style={{ color: "#888" }}>
  //       重大なエラーが発生しました。[{props.spread}]
  //     </h2>
  //   );
  // }

  // // 権限なし
  // if (meta !== undefined && !loadingMeta && !loadingData && userAllow === Global.MetaUserAllow.NONE && !errorMessage) {
  //   return <div>この帳票にはアクセスできません</div>;
  // }



  // 権限なし
  if (meta !== undefined  && !errorMessage) {
    if(meta === null) {
      return (
        <h2 style={{ color: "#888" }}>
          重大なエラーが発生しました。[{props.spread}]
        </h2>
      );
    } else if (userAllow === Global.MetaUserAllow.NONE) {
      return <div>この帳票にはアクセスできません</div>;
    }
  }

  //  エラー返却 ここまで ////////////////////////////////////////////////////////////////////////////////////////////////

  return (
    <div
      className={`spreadtable-container`}
    >

      {exportingExcel && (
        <WaitingSpinner message="Excel を生成しています…" />
      )}
      {exportingCSV && (
        <WaitingSpinner message="CSV を生成しています…" />
      )}
      {deleting && (
        <WaitingSpinner message="削除処理を実行しています…" />
      )}
      {faceProcessingCells.size > 0 && (
        <WaitingSpinner message="画像を処理中…しばらくお待ちください" />
      )}


      {/* エラーメッセージ表示 */}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
          type="error"
        />
      )}


      {modalMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
          onClick={() => alertModalMessage(null)}
        >
          <div
            style={{
              background: "white",
              padding: "16px 32px 10px",
              borderRadius: "8px",
              minWidth: "240px",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ marginBottom: "20px" }}>{modalMessage}</p>
            <div
              style={{
                borderTop: "1px solid #ddd",
                margin: "8px 10px 16px 10px",
              }}
            ></div>
            <button
              onClick={() => alertModalMessage(null)}
              style={{
                padding: "6px 32px",
                marginBottom: "16px"
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}


      { loadingMeta ? (
        <div>読み込み中...</div>
      ) : (
        <>
          {/* YAML定義の display をタイトルに */}
          <div className="spread-title-container" ref={titleRef}>
            {meta && meta.display && (
              <div className='page-title'>
                {meta.display}
                <span style={{ fontSize: "1.0em", marginLeft: "8px", color: "white" }}>
                  ［&nbsp;{meta.spread_id}
                  {meta.user_allow && meta.user_allow !== "NONE" 
                    ? ` : ${meta.user_allow}` 
                    : " : NONE"}
                  &nbsp;］
                </span>
              </div>
            )}
            {/* 条件ヘッダ / 検索ボタン */}
            {meta && meta.display && !isFormDisplay() ? (
              <>
                <div className="spread-filters-container" ref={filtersRef}>
                  {/* 固定条件部 */}
                  {props.base_filters && Object.keys(props.base_filters).length > 0 && (
                    <div className="fixed-filters" style={{ marginBottom: "8px" }}>
                      {Object.entries(props.base_filters)
                        .map(([k, v]) => `${k}:${renderFilterValue(k, v)}`)
                        .join(" / ")}
                    </div>
                  )}
                  {renderContextHeaders()}

                  {/* 任意条件部 */}
                  <div className="filters" onKeyDown={handleFilterKeyDown}>
                    {filterable.map((c:ColumnMeta) => (
                      // 条件項目
                      <div className="filter-item-box" key={c.celldata}>
                        {/* <span className='filter-label'>{c.display ?? c.celldata}</span> */}
                        <div className='filter-label'>{c.display ?? c.celldata}</div>
                        {Array.isArray(c.options) && c.options.length > 0 ? (
                          <select
                            style={{
                              minWidth: `${Global.getColumnWidth((c.length ?? 0)+2)}px`,
                              maxWidth: `${Global.getColumnWidth((c.length ?? 0)+2)}px`,
                              minHeight: `${Global.getRowHeight(1.2)}px`,
                              maxHeight: `${Global.getRowHeight(1.2)}px`,
                            }}

                            value={encodeOptionValue(searchBuffer[c.celldata] ?? "")}
                            onChange={e => setSearchBuffer(prev => ({ ...prev, [c.celldata]: resolveOptionValue(c.options, e.target.value) }))}
                            // value={searchBuffer[c.display] ?? ""}
                            // onChange={e => setSearchBuffer(prev => ({ ...prev, [c.display]: e.target.value }))}
                          >
                            {!c.ui_filter_required && <option value="">(全て)</option>}
                            {c.options.map((o:any) => {
                              // NULL を選べるように value="null" を明示
                              const v = encodeOptionValue(o.value);
                              return <option key={v} value={v}>{o.label}</option>;
                            })}
                          </select>
                        ) : c.type === "date" ? (
                          <DatePicker
                            locale="ja"
                            selected={parseDatePickerValue(searchBuffer[c.celldata])}
                            onChange={(date) => {
                              if (!date) {
                                setSearchBuffer(prev => ({ ...prev, [c.celldata]: "" }));
                                return;
                              }
                              setSearchBuffer(prev => ({ ...prev, [c.celldata]: formatDateValue(date) }));
                            }}
                            dateFormat="yyyy/MM/dd"
                            placeholderText="日付を選択"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            className="datepicker-input"
                          />
                        ) : (c.type === "datetime" || c.type === "sdatetime") ? (
                          <DatePicker
                            locale="ja"
                            selected={parseDatePickerValue(searchBuffer[c.celldata])}
                            onChange={(date) => {
                              if (!date) {
                                setSearchBuffer(prev => ({ ...prev, [c.celldata]: "" }));
                                return;
                              }
                              setSearchBuffer(prev => ({ ...prev, [c.celldata]: formatDateTimeValue(date) }));
                            }}
                            dateFormat="yyyy/MM/dd HH:mm"
                            showTimeSelect
                            timeIntervals={1}
                            timeCaption="時刻"
                            placeholderText="日時を選択"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            className="datepicker-input-dt"
                          />
                        ) : (c.type === "time" || c.type === "stime") ? (
                          <DatePicker
                            locale="ja"
                            selected={parseTimePickerValue(searchBuffer[c.celldata])}
                            onChange={(date) => {
                              if (!date) {
                                setSearchBuffer(prev => ({ ...prev, [c.celldata]: "" }));
                                return;
                              }
                              setSearchBuffer(prev => ({ ...prev, [c.celldata]: formatTimeValue(date) }));
                            }}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={1}
                            timeCaption="時刻"
                            placeholderText="時刻を選択"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            className="datepicker-input-dt"
                          />
                        ) : (
                          <input
                            style={{
                              minWidth: `${Global.getColumnWidth(c.length ?? 0)+1}px`,
                              maxWidth: `${Global.getColumnWidth(c.length ?? 0)+1}px`,
                              minHeight: `${Global.getRowHeight(0.9)}px`,
                              maxHeight: `${Global.getRowHeight(0.9)}px`,
                            }}
                            value={searchBuffer[c.celldata] ?? ""}
                            onChange={e => setSearchBuffer(prev => ({ ...prev, [c.celldata]: e.target.value }))}
                            // value={searchBuffer[c.display] ?? ""}
                            // onChange={e => setSearchBuffer(prev => ({ ...prev, [c.display]: e.target.value }))} 
                          />
                        )}
                      </div>
                    ))}

                    {/* 元の方式に戻す：これで useEffect が走って再検索 */}
                    {/* <button onClick={() => { setUiFilters(searchBuffer); }}>検索</button> */}
                    <div className="filter-item-box">
                      { outputType === Global.OutputType.SCREEN ? (
                        <>
                          {/* <button
                            onClick={() => {
                              // state更新（useEffectでのトリガーも維持）
                              setUiFilters({ ...searchBuffer });
                              // さらに同条件でも即時強制再検索
                              fetchData(searchBuffer);
                            }}
                          >
                            検索
                          </button> */}
                          <button onClick={handleSearchClick}>
                            検索
                          </button>                          

                          {/* nullキーで新規モードへ移行、入力値リセット */}
                          {/* <button onClick={() => {setFormPkey(Global.FormPkeyState.NEW); setEditValues({});}}>新規</button> */}
                          {/* nullキーで新規モードへ移行、入力値リセット */}
                          <button
                            onClick={() => {
                              if (userAllow === Global.MetaUserAllow.RW) {
                                setFormPkey(Global.FormPkeyState.NEW);
                                setEditValues({});
                              }
                            }}
                            className={(!isUserAllowReadAndWrite(userAllow) || meta?.new_button_disabled) ? "disabled-button" : ""} // ← グレー化
                            disabled={!isUserAllowReadAndWrite(userAllow) || !!meta?.new_button_disabled} // ← タップも無効
                          >
                            新規
                          </button>
                          {meta?.bulk_insert?.enabled && isUserAllowReadAndWrite(userAllow) && (
                            <button
                              onClick={() => {
                                // パネルが既に開いていて入力中の場合は、クリア前に確認する
                                const hasBulkInput =
                                  bulkRows.length > 1 ||
                                  bulkRows.some(r =>
                                    Object.entries(r).some(([k, v]) => k !== "_uid" && v !== "" && v != null)
                                  );
                                if (showBulkPanel && hasBulkInput) {
                                  if (!window.confirm("入力中の内容があります。クリアしてもよろしいですか？")) {
                                    return;
                                  }
                                }
                                setShowBulkPanel(true);
                                setBulkRows([{ _uid: Global.createClientUuid() }]);
                                setBulkRowErrors({});
                              }}
                            >
                              {meta.bulk_insert.button_title ?? "一括追加"}
                            </button>
                          )}
                          {/* <button onClick={() => fetchDataForCSV(searchBuffer)}>
                            検索してCSVに保存
                          </button> */}
                          {/* <button onClick={() => handleCsvExportClick(true)}>
                            検索してCSVに保存
                          </button>                         */}
                          {/* <button onClick={handleExportCurrentScreenCsv}>
                            CSVに保存
                          </button>                           */}
                          {meta?.show_csv_button && (
                            <button onClick={handleExportCurrentScreenCsv}>
                              CSVに保存
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {/* CSV出力 */}
                          {/* <button onClick={handleCsvExport}> */}
                          {/* <button onClick={() => fetchDataForCSV(searchBuffer)}>
                            検索してCSVに保存
                          </button> */}
                          <button onClick={() => handleCsvExportClick(false)}>
                            検索してCSVに保存
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => {
                          if (props.onClose) {
                            props.onClose();
                          } else {
                            // router.push("/");
                            router.back();
                          }
                        }}
                      >
                        閉じる
                      </button>

                    </div>

                  </div>
                </div>

              </>
            ) : (
              <>
              </>
            )}
          </div>

          {/* 一括追加パネル */}
          {showBulkPanel && meta && (
            <div style={{ margin: "8px 0", border: "1px solid #ccc", borderRadius: "8px", padding: "12px", background: "#fafafa" }}>
              <div style={{ fontWeight: "bold", color: "royalblue", marginBottom: "8px" }}>
                一括追加: {meta.display}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <thead>
                    <tr>
                      {visibleBulkColumns.map((col) => (
                        <th key={col.celldata} style={{ border: "1px solid #ccc", padding: "4px 8px", background: "#f0f0f0", whiteSpace: "nowrap" }}>
                          {col.display}{isRequired(col, editValues) && <span style={{ color: "red", marginLeft: "2px" }}>*</span>}
                        </th>
                      ))}
                      <th style={{ border: "1px solid #ccc", padding: "4px 8px", background: "#f0f0f0", width: "32px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((row, rowIdx) => (
                      <tr key={row._uid ?? rowIdx}>
                        {visibleBulkColumns.map((col) => {
                          const hasErr = !!bulkRowErrors[rowIdx]?.[col.celldata];
                          const cellStyle: React.CSSProperties = {
                            border: "1px solid #ccc",
                            padding: "4px",
                            verticalAlign: "top",
                          };
                          return (
                            <td key={col.celldata} style={cellStyle}>
                              {Array.isArray(col.options) && col.options.length > 0 ? (
                                <select
                                  value={row[col.celldata] ?? ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setBulkRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [col.celldata]: val } : r));
                                    if (hasErr || bulkRowErrors[rowIdx]) setBulkRowErrors(prev => {
                                      const next = { ...prev };
                                      if (next[rowIdx]) {
                                        if (requiredBulkColumns.length === 0) {
                                          // all-empty ブランチ: 行全体のエラーをクリア
                                          delete next[rowIdx];
                                        } else {
                                          delete next[rowIdx][col.celldata];
                                          if (!Object.keys(next[rowIdx]).length) delete next[rowIdx];
                                        }
                                      }
                                      return next;
                                    });
                                  }}
                                  style={{ border: hasErr ? "1px solid red" : "1px solid #ccc", borderRadius: "4px", padding: "2px 4px", minWidth: `${Global.getColumnWidth(col.length ?? 8)}px` }}
                                >
                                  <option value="">(未選択)</option>
                                  {col.options.map((opt: any) => {
                                    const v = (opt.value === null || opt.value === undefined) ? "null" : String(opt.value);
                                    return <option key={v} value={v}>{opt.label}</option>;
                                  })}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={row[col.celldata] ?? ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setBulkRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [col.celldata]: val } : r));
                                    if (hasErr || bulkRowErrors[rowIdx]) setBulkRowErrors(prev => {
                                      const next = { ...prev };
                                      if (next[rowIdx]) {
                                        if (requiredBulkColumns.length === 0) {
                                          // all-empty ブランチ: 行全体のエラーをクリア
                                          delete next[rowIdx];
                                        } else {
                                          delete next[rowIdx][col.celldata];
                                          if (!Object.keys(next[rowIdx]).length) delete next[rowIdx];
                                        }
                                      }
                                      return next;
                                    });
                                  }}
                                  style={{ border: hasErr ? "1px solid red" : "1px solid #ccc", borderRadius: "4px", padding: "2px 4px", width: `${Global.getColumnWidth(col.length ?? 8)}px` }}
                                />
                              )}
                              {hasErr && (
                                <div style={{ color: "red", fontSize: "0.8em", marginTop: "2px" }}>
                                  ⚠ {bulkRowErrors[rowIdx][col.celldata]}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td style={{ border: "1px solid #ccc", padding: "4px", textAlign: "center", verticalAlign: "top" }}>
                          <button
                            onClick={() => {
                              setBulkRows(prev => prev.filter((_, i) => i !== rowIdx));
                              setBulkRowErrors(prev => {
                                const next: Record<number, Record<string, string>> = {};
                                Object.entries(prev).forEach(([k, v]) => {
                                  const oldIdx = Number(k);
                                  if (oldIdx < rowIdx) next[oldIdx] = v;
                                  else if (oldIdx > rowIdx) next[oldIdx - 1] = v;
                                });
                                return next;
                              });
                            }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: "1.1em" }}
                          >✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                <button onClick={() => setBulkRows(prev => [...prev, { _uid: Global.createClientUuid() }])}>+ 行を追加</button>
                <button onClick={() => { setShowBulkPanel(false); setBulkRows([]); setBulkRowErrors({}); }}>キャンセル</button>
                <button
                  onClick={handleBulkSave}
                  disabled={bulkSaving || bulkRows.length === 0}
                  className={bulkSaving || bulkRows.length === 0 ? "disabled-button" : ""}
                >
                  {bulkSaving ? "保存中..." : `まとめて保存 (${bulkRows.length}件)`}
                </button>
              </div>
            </div>
          )}

          <div
            className={
              "spread-main-container" +
              (isFormDisplay() || isCSVOutput() ? " spread-main-container--hidden" : "")
            }
          >
            {/* テーブル部 */}
            {meta === undefined ? (
              // ほんの一瞬来ることがある
              <div>読み込み中...</div>
            ) : loadingData ? (
              <div>読み込み中...</div>
            ) : !data || data.length === 0 ? (
              <div>データがありません。</div>
            ) : (
              <div className="spreadtable-wrapper" ref={wrapperRef}>
                <table className="spreadtable" style={{
                            width: `${Global.getColumnWidth(1.5)
                              + Global.getColumnWidth(6)
                              + totalColumnWidth}px`,
                        }} >
                  <thead>
                    <tr>
                      <th className='keeplefttop' style={{width: `${Global.getColumnWidth(1.2)}px`, left: `${0}px`}}>#</th>

                      <th className='keeplefttop' style={{borderWidth: "1px 1px 1px 0px", width: `${Global.getColumnWidth(4.0)}px`, left: `${Global.getColumnWidth(1.2) + listActionLeftPad}px`}}>操作</th>
                      {visibleListColumns.map((col, colIndex) => {
                        const htmlDisplay = (col.display || col.celldata || "").replace(/\n/g, "<br />");
                        const listColumnKey = getListColumnKey(col, colIndex);
                        const sticky = listFixedLeftMap.get(listColumnKey);
                        return (
                          <th
                            key={listColumnKey}
                            className={sticky ? "keeplefttop" : undefined}
                            style={{
                              width: `${Global.getColumnWidth(col.length ?? 0)}px`,
                              backgroundColor: col.col_backcolor ? col.col_backcolor : undefined,
                              textAlign: "center",
                              ...(sticky ? { left: `${sticky.left}px`, zIndex: sticky.headerZIndex } : {}),
                            }}
                            dangerouslySetInnerHTML={{ __html: htmlDisplay }}
                          />
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {data.map((row, rowIndex) => {
                      if (!row || typeof row !== "object") return null;
                      const isRowEditing = editingRow === rowIndex;
                      const isRowSelected = selectedRow === rowIndex;
                      const lineEditChanged = (() => {
                        if (!isRowEditing || !meta?.pkey) return false;
                        const edited = edits[rowIndex] || {};
                        const merged = { ...row, ...edited };
                        return (getChangedValues(row, merged, meta.pkey)._changed as string[]).length > 0;
                      })();
                      const selectedRowBackground = isRowSelected ? "#d0ebff" : "transparent";
                      const stickyCellBackground = isRowSelected ? "#d0ebff" : "#ffffff";
                      
                      return (
                        <tr
                          key={rowIndex}
                          ref={isRowSelected ? (el) => { selectedRowRef.current = el; } : undefined}
                          onClick={(e) => {
                            // 操作ボタンのクリックは除外（イベントバブリング防止）
                            if ((e.target as HTMLElement).tagName === "BUTTON" || (e.target as HTMLElement).tagName === "INPUT" || (e.target as HTMLElement).tagName === "SELECT") {
                              return;
                            }
                            setSelectedRow(rowIndex);
                            props.onRowClick?.({ row, rowIndex });
                          }}
                          style={{
                            backgroundColor: selectedRowBackground,
                            cursor: "pointer"
                          }}
                        >
                          {/* 行番号 */}
                          <td className='keepleft' style={{ left: `${0}px`, backgroundColor: stickyCellBackground}}>{rowIndex + 1}</td>

                          {/* 操作ボタン */}
                          <td className='keepleft' style={{ borderWidth: "0px 1px 1px 0px", left: `${Global.getColumnWidth(1.2) + listActionLeftPad}px`, backgroundColor: stickyCellBackground}}>
                            {isRowEditing ? (
                              <>
                                {/* 保存 */}
                                <i 
                                  title="保存" 
                                  className={`fas fa-save spread-tool-awesome ${!lineEditChanged ? "disabled-icon" : ""}`}
                                  onClick={() => lineEditChanged && handleSave(rowIndex)} />
                                {/* キャンセル */}
                                <i 
                                  title="キャンセル" 
                                  className="fas fa-times spread-tool-awesome" 
                                  onClick={handleCancel} />
                              </>
                            ) : (
                              <>

                                {/* フォーム編集 */}
                                <i 
                                  title="フォーム表示" 
                                  className="fas fa-file-lines spread-tool-awesome" 
                                  onClick={(e) => handleDetail(e, rowIndex)} />

                                {/* ライン編集：権限により制御 */}
                                <i
                                  title="行編集" 
                                  className={`fas fa-pen spread-tool-awesome ${!isUserAllowReadAndWrite(userAllow) ? "disabled-icon" : ""}`}
                                  onClick={() => isUserAllowReadAndWrite(userAllow) && handleEdit(rowIndex)}
                                />

                                {/* 削除：権限により制御 */}
                                <i
                                  title="削除" 
                                  className={`fas fa-trash spread-tool-awesome ${(!isUserAllowReadAndWrite(userAllow) || meta?.del_button_disabled) ? "disabled-icon" : ""}`}
                                  onClick={() => isUserAllowReadAndWrite(userAllow) && !meta?.del_button_disabled && handleDelete(rowIndex)}
                                />
                              </>

                            )}
                          </td>

                          {/* 通常カラム */}
                          {visibleListColumns.map((col, colIndex) => {
                            const value = isRowEditing ? (editBuffer[col.celldata] ?? row[col.celldata]) : row[col.celldata];
                            const colMeta = col;
                            const listColumnKey = getListColumnKey(col, colIndex);
                            const sticky = listFixedLeftMap.get(listColumnKey);
                            const isLinkColumn = colMeta.type === "link";
                            const isMultiLineListEdit = isRowEditing && Number(col.rows ?? 1) > 1;
                            const listCellDepth = Number(col.depth ?? 1);
                            const listCellTextareaHeight = Global.getRowHeight(listCellDepth);
                            const listCellEditTopPadding = 6;
                            const listCellValues = isRowEditing ? { ...row, ...editBuffer } : row;

                            return(

                              <td
                                key={listColumnKey}
                                className={sticky ? "keepleft" : undefined}
                                style={{
                                  backgroundColor: sticky ? stickyCellBackground : undefined,
                                  ...getForeColorStyle(col, listCellValues),
                                  ...getListAlignStyle(col.list_align),
                                  ...(isLinkColumn ? { textAlign: "center" as const } : {}),
                                  ...(isMultiLineListEdit ? { height: "auto", padding: 0, verticalAlign: "top" as const } : {}),
                                  ...(sticky ? { left: `${sticky.left}px`, zIndex: sticky.bodyZIndex } : {}),
                                }}
                              >
                                {(() => {
                                  const value = isRowEditing ? (editBuffer[col.celldata] ?? row[col.celldata]) : row[col.celldata];
                                  if (isHidden(colMeta, row)) return null;

                                  // セレクトボックス（options）
                                  if (colMeta.options) {
                                    const opts = (colMeta.options || []).map((o: any) => ({
                                      value: (o.value === null || o.value === undefined) ? "null" : String(o.value),
                                      label: o.label
                                    }));
                                    const rawCur = editBuffer[col.celldata] ?? value;
                                    const cur = (rawCur === null || rawCur === undefined) ? "null" : String(rawCur);

                                    return isRowEditing ? (
                                      <select
                                        value={cur}
                                        style={{
                                          minWidth: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                          maxWidth: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                          minHeight: `${Global.getRowHeight(1)}px`,
                                          maxHeight: `${Global.getRowHeight(1)}px`,
                                          ...getForeColorStyle(col, listCellValues),
                                          ...getListAlignStyle(col.list_align),
                                        }}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setListCell(rowIndex, row, col.celldata, v);
                                        }}
                                        // disabled={props.base_filters && props.base_filters.hasOwnProperty(col.celldata)}
                                        disabled={isReadOnly(col, { ...row, ...editBuffer })}
                                      >
                                        <option value="">(未選択)</option>
                                        {opts.map((opt: any) => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      // colMeta.options.find((opt: any) => opt.value == row[col.celldata])?.label || row[col.celldata]
                                      // セレクトの内容を検索して、値を表示する
                                      // DB NULL は sel_query の 'null' オプションに一致させる（'null' == null は false になるため文字列に正規化して比較）
                                      <span style={getListAlignStyle(col.list_align)}>
                                        {colMeta.options.find((opt: any) =>
                                          String(opt.value) === ((row[col.celldata] ?? null) === null ? "null" : String(row[col.celldata]))
                                        )?.label}
                                      </span>
                                    );

                                  }

                                  // LINKカラム描画（ここを置き換え）
                                  if (colMeta.type === 'link') {
                                    const cell = row[colMeta.celldata];                 // 表示用
                                    const displayText =
                                      (cell && typeof cell === 'object' && 'display' in cell ? cell.display : undefined)
                                      ?? (colMeta.display ?? '➡');
                                    const linkDisabled = isReadOnly(colMeta, row);
                                    const linkButtonClass = `spread-button spread-list-link-button${linkDisabled ? " disabled-button" : ""}`;

                                    // spread定義はYAMLメタから取得。base_filters/form_pkeyはバックエンド解決済みを優先
                                    //let linkData: any = colMeta.link;
                                    let linkData: ColumnLinkMeta = colMeta.link ?? {};
                                    if (!linkData && meta?.columns) {
                                      const metaCol = meta.columns.find((c: any) => c.celldata === colMeta.celldata);
                                      linkData = metaCol?.link;
                                    }

                                    // デバッグ
                                    //console.log('link(render):', JSON.stringify(linkData));

                                    if (!linkData) return <span>{displayText}</span>;

                                    // バックエンドが解決済みの link_info を優先（{USER_GUID} 等のプレースホルダ解決済み）
                                    // フォールバック: 列名→行値 置換（従来の動作）
                                    const cellLink = (cell as any)?.link;
                                    const resolvedParams = cellLink?.base_filters != null
                                      ? cellLink.base_filters
                                      : Object.fromEntries(
                                          Object.entries(linkData.base_filters ?? {}).map(([k, v]) => {
                                            const fromRow = typeof v === 'string' ? row[v] : undefined;
                                            return [k, fromRow ?? v];
                                          }),
                                        );
                                    const resolvedUiFilters = cellLink?.ui_filters != null
                                      ? cellLink.ui_filters
                                      : Object.fromEntries(
                                          Object.entries(linkData.ui_filters ?? {}).map(([k, v]) => {
                                            const fromRow = typeof v === 'string' ? row[v] : undefined;
                                            return [k, fromRow ?? v];
                                          }),
                                        );
                                    //console.log('🟡 resolvedParams:', resolvedParams);

                                    if (linkData.spread) {
                                      return (
                                        <button
                                          className={linkButtonClass}
                                          disabled={linkDisabled}
                                          onClick={(e) => {
                                            e.stopPropagation();  // ← まず tr のクリックイベントを止める
                                            if (linkDisabled) return;
                                            setSelectedRow(rowIndex);  // ← ボタン押下でも選択色を付ける
                                            openChild(linkData.spread ?? null, {
                                              base_filters: resolvedParams,
                                              ...(Object.keys(resolvedUiFilters).length > 0 ? { ui_filters: resolvedUiFilters } : {}),
                                              form_pkey: cellLink ? (cellLink.form_pkey ?? Global.FormPkeyState.NONE) : (linkData.form_pkey ?? Global.FormPkeyState.NONE),
                                            });
                                          }
                                          }
                                          >
                                          {changeLinkText(displayText, getEffectiveForeColor(colMeta, listCellValues))}
                                          {/* {changeLinkText(displayText, rowIndex)} */}
                                        </button>
                                      );
                                    }

                                    // stored_proc ボタン処理（link.stored_proc が定義されている列のみ）
                                    if (linkData.stored_proc) {
                                      return (
                                        <button
                                          className={linkButtonClass}
                                          disabled={linkDisabled}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (linkDisabled) return;
                                            setSelectedRow(rowIndex);
                                            const confirmed = window.confirm(
                                              `「${colMeta.display}」を実行します。よろしいですか？`
                                            );
                                            if (!confirmed) return;
                                            try {
                                              const res = await axios.post<any>(
                                                `${Global.getApiBaseUrl()}/spread/exec_stored_proc`,
                                                {
                                                  base_token_interface: baseTokenInterface(),
                                                  body_data: {
                                                    spread_id: String(props.spread),
                                                    key_value: row[meta?.pkey ?? ""],
                                                    col_name: colMeta.celldata,
                                                  },
                                                }
                                              );
                                              alert(res.data.message ?? "処理が完了しました。");
                                              await refreshParentRowByKey(String(row[meta?.pkey ?? ""]));
                                            } catch (err: any) {
                                              alert(`エラー: ${err?.response?.data?.detail ?? (err as Error).message}`);
                                            }
                                          }}
                                        >
                                          {changeLinkText(displayText, getEffectiveForeColor(colMeta, listCellValues))}
                                        </button>
                                      );
                                    }

                                    // EXCELテンプレートを使用する場合
                                    if (linkData.action?.action_type === "excelbuilder") {
                                      return (
                                        <button
                                          className={`spread-button spread-list-link-button${(linkDisabled || exportingExcel) ? " disabled-button" : ""}`}
                                          onClick={(e) => {

                                            e.stopPropagation();  // ← まず tr のクリックイベントを止める
                                            if (linkDisabled || exportingExcel) return;
                                            setSelectedRow(rowIndex);  // ← ボタン押下でも選択色を付ける
                                            // alert(`リンクパラメータ: ${JSON.stringify(linkData, null, 2)}`);
                                            //const resolvedFilters = resolveActionFilters(row, linkData);
                                            const resolvedPKey = resolveActionPkey(row, linkData);
                                            //alert(resolvedPKey);
                                            handleExcelExport(row, linkData, resolvedPKey);
                                          }}
                                          disabled={linkDisabled || exportingExcel}                                 // ★ 連打防止
                                          style={{ opacity: (linkDisabled || exportingExcel) ? 0.6 : 1 }}
                                        >
                                          {changeLinkText(displayText, getEffectiveForeColor(colMeta, listCellValues))}
                                          {/* {changeLinkText(displayText, rowIndex)} */}
                                        </button>
                                      );
                                    }
                                    if (linkData.action?.action_type === "aisupport" || linkData.action?.action_type === "voicerecording") {
                                      // console.log(`リンクパラメータ: ${JSON.stringify(linkData)}`);
                                      return (
                                        <button
                                          className={linkButtonClass}
                                          disabled={linkDisabled}
                                          //onClick={() => openChild(row, { ...resolvedParams, __action: linkData.action })}
                                          //onClick={() => {alert(`リンクパラメータ: ${JSON.stringify(linkData.action_filters)}`);}}
                                          //onClick={() => openChild(null, { ...resolvedParams, __action: linkData.action, ...(linkData.action?.action_filters ?? {}) })}
                                          onClick={(e) => {
                                            e.stopPropagation();  // ← まず tr のクリックイベントを止める
                                            if (linkDisabled) return;
                                            setSelectedRow(rowIndex);  // ← ボタン押下でも選択色を付ける
                                            // const resolvedFilters = resolveActionFilters(row, linkData);
                                            const resolvedPKey = resolveActionPkey(row, linkData);
                                            const action = {
                                              ...linkData.action,
                                              action_pkey: resolvedPKey, // ← 実値に置き換え
                                              action_template: linkData.action?.action_template, // ← 実値に置き換え
                                            };
                                            // openChild(null, {
                                            //    ...resolvedParams,
                                            //    __action: action
                                            // });
                                            openChild(null, {
                                              base_filters: resolvedParams,
                                              __action: action,
                                            });



                                          }}

                                          >
                                          {changeLinkText(displayText, getEffectiveForeColor(colMeta, listCellValues))}
                                          {/* {changeLinkText(displayText, rowIndex)} */}
                                        </button>
                                      );
                                    }

                                  } else if (colMeta.type === 'binary') {
                                    // バイナリサムネイル表示
  const bin_guid = row[col.celldata];

  return bin_guid ? (

    // <div className="binary-thumb-container">
    //   <img
    //     src={`${Global.getApiBaseUrl()}/spread/get_binary_thumbnail?user_domain=${employeeDomain}&bin_guid=${bin_guid ?? ""}`}
    //     className="binary-thumb-img-list"
    //   />
    // </div>
    <BinaryViewer
      user_domain={employeeDomain ?? ""}
      bin_guid={bin_guid ?? ""}
      binaryType={Global.BinaryType.LOW}
    />

  ) : null;

                                  } else if (!isRowEditing) {
                                    // 通常INPUT欄のラベル表示（非編集）
                                    // return row[col.celldata]
                                    return (
                                      // 値だけで強制一行表示にしないと、"…"表現が無効にになる。
                                      String((getDisplayValue(col, row[col.celldata]) ?? "")).replace(/\n/g, "")
                                      // String((row[col.celldata] ?? "")).replace(/\n/g, "<br />")
                                      // <div 
                                      //   className='spread-cell-label'
                                      //   style={{
                                      //     minWidth: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                      //     maxWidth: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                      //     minHeight: `${Global.getRowHeight(col.depth ?? 1, undefined, true)}px`,
                                      //     maxHeight: `${Global.getRowHeight(col.depth ?? 1, undefined, true)}px`,
                                      //   }}
                                      //   dangerouslySetInnerHTML={{ __html: String((row[col.celldata] ?? "")).replace(/\n/g, "<br />")}} 
                                      // />
                                    )
                                  } else {
                                    const listEditRows = Number(col.rows ?? 1);
                                    if (listEditRows <= 1) {
                                    // rowsが複数行であれば、１行表示でも、TEXTAREAにする必要があるから
                                    // inputに入らないようにする
                                      return (
                                      // インプットテキスト
                                        <input
                                          type="text"
                                          style={{
                                            minWidth: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                            maxWidth: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                            minHeight: `${Global.getRowHeight(1)}px`,
                                            maxHeight: `${Global.getRowHeight(1)}px`,
                                          ...getForeColorStyle(col, listCellValues),
                                            ...getListAlignStyle(col.list_align),
                                          }}
                                        value={editBuffer[col.celldata] ?? ""}
                                        onChange={(e) => {
                                          const newValue = e.target.value;
                                          setListCell(rowIndex, row, col.celldata, newValue);
                                        }}
                                        // readOnly={props.base_filters && props.base_filters.hasOwnProperty(col.celldata)}
                                        readOnly={isReadOnly(col, { ...row, ...editBuffer })}
                                      />
                                    )
                                    }
                                      return (
                                      // テキストエリア
                                        <div
                                          style={{
                                            width: `${Global.getColumnWidth(col.length ?? 0)}px`,
                                            height: `${listCellTextareaHeight + listCellEditTopPadding}px`,
                                            boxSizing: "border-box",
                                            padding: `${listCellEditTopPadding}px 2px 0px 2px`,
                                            display: "block",
                                            overflow: "hidden",
                                          }}
                                        >
                                          <textarea
                                            // value={editValues[col.celldata] ?? ""}
                                            value={editBuffer[col.celldata] ?? ""}
                                            wrap="off"
                                            style={{
                                              width: `${Global.getColumnWidth(col.length ?? 0) - 4}px`,
                                              height: `${listCellTextareaHeight}px`,
                                              minHeight: `${listCellTextareaHeight}px`,
                                              maxHeight: `${listCellTextareaHeight}px`,
                                              boxSizing: "border-box",
                                              padding: "2px 4px",
                                              display: "block",
                                              margin: 0,
                                              resize: "none",
                                              whiteSpace: "pre",
                                              overflowX: "auto",
                                              overflowY: "auto",
                                          ...getForeColorStyle(col, listCellValues),
                                              ...getListAlignStyle(col.list_align),
                                            }}
                                            onChange={(e) => {
                                              const newValue = e.target.value;
                                              setListCell(rowIndex, row, col.celldata, newValue);
                                            }}
                                            // readOnly={props.base_filters && props.base_filters.hasOwnProperty(col.celldata)}
                                            readOnly={isReadOnly(col, { ...row, ...editBuffer })}
                                          />
                                        </div>
                                      )
                                  }
                                  return null;
                                })()}
                              </td>

                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

              </div>
            )}
          </div>


        </>
      )}

      {/* ここからフォーム */}
      {!loadingMeta && isFormDisplay() && (
        <div className="spread-form-container">
          {meta === undefined ? (
            // ほんの一瞬来ることがある
            <div>読み込み中...</div>
          ) : loadingData ? (
            // 通常ここには来ない
            <div>読み込み中...</div>
          ) : (
            <form
              onSubmit={e => {
                e.preventDefault();
                e.stopPropagation();
                // ここでは何もしない：submit＝無効
              }}
            >
              {/* ヘッダーボタン部 */}
              <div className="spread-form-button-container" ref={formButtonRef}>
                {/* { alert(props.form_pkey)} */}
                {isOpenedFromList() && (
                  <button type="button" onClick={closeFormOnly}>
                    リストへ戻る
                  </button>
                )}

                {isFormEditing ? (
                  <>

                    {isFormEditing && (
                      <>
                        {/* 前に戻る：STEP_FREE のみ、かつ先頭以外 */}
                        {formCursorType === Global.FormCursorType.STEP_FREE && !isFirstStep && (
                          <button type="button" onClick={handlePrev}>
                            前に戻る
                          </button>
                        )}

                        {/* 次に進む：STEP系で、最後でなければ */}
                        {(formCursorType === Global.FormCursorType.STEP_FORWARD_ONLY ||
                          formCursorType === Global.FormCursorType.STEP_FREE) &&
                          !isLastStep && (
                            <button type="button" onClick={handleNext}>
                              次に進む
                            </button>
                        )}

                        {formPkey !== Global.FormPkeyState.NEW && !isStepFormCursor && (
                          <button type="button" onClick={handleReturnToDisplay}>
                            表示に戻る
                          </button>
                        )}
                        {/* 保存する：ALL か、STEPで最後の設問のとき */}
                        {(
                          formCursorType === Global.FormCursorType.ALL_CONFIRM ||
                          formCursorType === Global.FormCursorType.ALL_CONFIRM_SKIP ||
                          isLastStep
                        ) && (
                          <button
                            type="button"
                            disabled={isSaving || !hasChanges}
                            className={!hasChanges ? "disabled-button" : undefined}
                            style={{ color: hasChanges ? undefined : "gray" }}
                            onClick={(e) => {
                              if (!hasChanges) return;
                              handleFormSave(e);
                            }}
                          >
                            {isSaving ? "保存中…" : "保存する"}
                          </button>
                        )}
                      </>
                    )}


                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={!isUserAllowReadAndWrite(userAllow) ? "disabled-button" : ""}
                      onClick={() => {
                          // setFormEditing(true);
                          handleStartEdit();
                      }}
                      disabled={!isUserAllowReadAndWrite(userAllow)}
                    >
                      編集を開始する
                    </button>


                  </>





                )}
              </div>

              {/* フォーム項目の表示 */}
              <div className="spread-form-wrapper-container" ref={formWrapperRef}>
                {/* {visibleFormColumns.filter(col => col.type !== "link").map(col => ( */}
                {inlineGroups.map((group, index) => (

                  <div key={index} className="spread-form-group-container">
                    {group.cols.map(col => (

                    <div className="spread-form-columns-block" key={col.celldata} style={{paddingBottom: '8px'}}>
                      {/* 見出し表示 */}
                      {col.type !== "label" && (
                        // <>
                        //   <label className="col_label"><span>🟩{col.display}</span></label>
                        // </>
                          <div className="col_label" dangerouslySetInnerHTML={{ __html: '🟩'+(col.display ?? "").replace(/\n/g, "<br />") }}></div>

                      )}

                      {/* 音声合成 */}
                      {!isFormEditing && col.voiceplayer && (
                        <>
                          <PlayerVoice
                            // employeeGUID={employeeGUID ?? ""}
                            text={editValues[col.celldata]}
                            voiceType={col.voiceplayer.split(";")[0]}
                            speed={
                              col.voiceplayer.split(";")[1]
                                ? parseFloat(col.voiceplayer.split(";")[1])
                                : 1.0
                            }
                          />
                        </>
                      )}

                      {/* 値表示 */}
                      {col.type == "label" ? (
                        // ラベル表示
                        <>
                          <div dangerouslySetInnerHTML={{ __html: col.labelvalue ?? "" }}></div>
                        </>
                      ) :  col.type === "binary" ? (

                        
  (() => {
    const bin_guid = editValues[col.celldata];

    return (
      <div key={col.temp_uid} className="form-item">
        {/* <label className="form-label">{col.display}</label> */}

        {isFormEditing && (
          <input
            type="file"
            accept="*/*"
            onChange={(e) => handleBinaryFileSelect(e, col.celldata)}
            disabled={isReadOnly(col)}
          />
        )}

        {/* bin_guid 表示 */}
        {bin_guid && (
          <div style={{ fontSize: "0.9em", marginTop: "4px" }}>
            {/* bin_guid: {bin_guid} */}

            {/* <div className="binary-thumb-container">
              <img
                src={`${Global.getApiBaseUrl()}/spread/get_binary_thumbnail?user_domain=${employeeDomain}&bin_guid=${bin_guid}`}
                className="binary-thumb-img-form"
              />
            </div> */}
            <BinaryViewer
              user_domain={employeeDomain ?? ""}
              bin_guid={editValues[col.celldata]}
              binaryType={Global.BinaryType.MID}
            />




          </div>
        )}


      </div>
    );
  })()


                      ) :  col.type.startsWith("base64face") ? (
                        // 画像表示項目
                        <div className="col_value">
                          {isFormEditing ? (
                            <div style={{ alignItems: 'center', gap: '12px' }}>
                              {faceProcessingCells.has(col.celldata) ? (
                              // 顔画像の加工(face_preview)処理中：再アップロード不可・処理中表示
                              <div className="face-placeholder">処理中…</div>
                              ) : editValues[col.celldata] ? (
                              // 編集モード既存写真あり
                              <>
                                  {/* # 顔画像編集 ここから ##################################################### */}
                                  <div className='face-image-wrap'>
                                    <img 
                                      className='face-image' 
                                      src={`data:image/webp;base64,${editValues[col.celldata]}`}
                                      //src={`${editValues[col.celldata]}`}
                                      alt={col.display}
                                    />
                                    <div className="face-button-group">
                                      <div className="face-icon-wrapper" title="削除"
                                        onClick={() => {
                                          handleEditValueChange(col.celldata, "");  
                                        }}
                                      >
                                        <i className="fas fa-trash fa-icon"></i>
                                      </div>
                                    </div>
                                  </div>
                                  {/* # 顔画像編集 ここまで ##################################################### */}
                                </>
                              ) : (
                              // 編集モード既存写真なし
                                <>
                                  {/* # 顔画像編集 ここから ##################################################### */}
                                  <div className="face-placeholder">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      ref={(el) => {
                                        faseFileInputRefs.current[col.celldata] = el;
                                      }}
                                      style={{ display: "none" }}
                                      onChange={(e) => {
                                        handleFormFaceImageSelect(e, col.celldata);
                                      }}
                                    />
                                    <div className="face-button-group">
                                      <div
                                        className="face-icon-wrapper"
                                        title="アップロード"
                                        onClick={() => faseFileInputRefs.current[col.celldata]?.click()}
                                      >
                                        <i className="fas fa-file-upload fa-icon"></i>
                                      </div>

                                      <div
                                        className="face-icon-wrapper"
                                        title="カメラ"
                                        onClick={() => cameraInputRefs.current[col.celldata]?.click()}
                                        style={{ display: isIOS ? "block" : "none" }}  // ← Safari 以外は消える
                                      >
                                        <i className="fas fa-camera fa-icon"></i>
                                      </div>

                                      <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        ref={(el) => {
                                          cameraInputRefs.current[col.celldata] = el;
                                        }}
                                        style={{ display: "none" }}
                                        onChange={(e) => handleFormFaceImageSelect(e, col.celldata)}
                                      />

                                    </div>
                                    NO DATA
                                  </div>
                                  {/* # 顔画像編集 ここまで ##################################################### */}
                                </>
                              )}
                            </div>
                          ) : (
                            // 通常ラベル的表示のみ
                            <div style={{ alignItems: 'center', gap: '12px' }}>
                              {editValues[col.celldata] ? (
                                // # 顔画像表示 ここから #####################################################
                                <div className='face-image-wrap'>
                                  <img 
                                    className='face-image' 
                                    //src={`${editValues[col.celldata]}`}
                                    src={`data:image/webp;base64,${editValues[col.celldata]}`}
                                    alt={col.display}
                                  />
                                </div>
                                // # 顔画像表示 ここまで #####################################################
                              ) : (
                                <span style={{ color: '#aaa', fontSize: '0.85em' }}>(未登録)</span>
                              )}
                            </div>
                          )}

                        </div>
                      ) : (col.type == "date" ) && isFormEditing ? (
                        <div className="col_value">
                          <DatePicker
                            locale="ja"
                            selected={parseDatePickerValue(editValues[col.celldata])}
                            onChange={(date) => {
                              if (!date) return;
                              handleEditValueChange(col.celldata, formatDateValue(date));
                            }}  
                            dateFormat="yyyy/MM/dd"
                            placeholderText="日付を選択"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            className="datepicker-input"
                            disabled={isReadOnly(col)}
                          />
                        </div>
                      ) : (col.type == "datetime" || col.type == "sdatetime") && isFormEditing ? (
                        <div className="col_value">
                          <DatePicker
                            locale="ja"
                            selected={parseDatePickerValue(editValues[col.celldata])}
                            onChange={(date) => {
                              if (!date) return;
                              handleEditValueChange(col.celldata, formatDateTimeValue(date));
                            }}
                            dateFormat="yyyy/MM/dd HH:mm"   // ← 時刻も含める
                            showTimeSelect                // ← これを追加
                            timeIntervals={1}            // ← 時刻の間隔（分単位）
                            timeCaption="時刻"             // ← タイムピッカーの見出し
                            placeholderText="日時を選択"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            className="datepicker-input-dt"
                            disabled={isReadOnly(col)}
                          />
                        </div>
                      ) : (col.type == "time" || col.type == "stime") && isFormEditing ? (
                        <div className="col_value">
                          <DatePicker
                            locale="ja"
                            selected={parseTimePickerValue(editValues[col.celldata])}
                            onChange={(date) => {
                              if (!date) return;
                              handleEditValueChange(col.celldata, formatTimeValue(date));
                            }}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={1}
                            timeCaption="時刻"
                            placeholderText="時刻を選択"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            className="datepicker-input-dt"
                            disabled={isReadOnly(col)}
                          />
                        </div>
                      ) :  Array.isArray(col.options) && col.options.length > 0 ?  (
                        <div className="col_value">
                          {isFormEditing ? (
                            // セレクトボックス項目入力
                            <select
                              value={getFormValue(col)}
                              onChange={(e) => handleEditValueChange(col.celldata, e.target.value)}
                              disabled={isReadOnly(col)}
                              style={getForeColorStyle(col)}
                            >
                              <option value="">(未選択)</option>
                              {col.options.map((opt: any) => {
                                const v = (opt.value === null || opt.value === undefined) ? "null" : String(opt.value);
                                return <option key={v} value={v}>{opt.label}</option>;
                              })}
                            </select>
                          ) : (
                            // セレクトボックス項目をラベル表示
                            <span style={getForeColorStyle(col)}>
                              {
                                col.options.find(
                                  (opt: any) => String(opt.value) === String(editValues[col.celldata])
                                )?.label || <span style={{ color: "#888" }}>(未選択)</span>
                              }
                            </span>
                          )}
                        </div>
                      ) : (
                        // 通常の一般入力ボックスの編集状態
                        <div className="col_value">
                          {(col.rows === null || col.rows === undefined || col.rows === 1) ? (
                            isFormEditing ? (
                              // テキストボックス項目入力
                              <input
                                type="text"
                                // value={editValues[col.celldata] ?? ""}
                                value={getFormValue(col)}
                                style={{
                                  minWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  maxWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  minHeight: `${Global.getRowHeight(1)}px`,
                                  maxHeight: `${Global.getRowHeight(1)}px`,
                                  ...getForeColorStyle(col),
                                }}
                                onChange={(e) => handleEditValueChange(col.celldata, e.target.value)}
                                // readOnly={props.base_filters && props.base_filters.hasOwnProperty(col.celldata)}
                                readOnly={isReadOnly(col)}
                              />
                            ) : (
                              // テキストボックス項目をラベル表示
                              <div className="spread-form-label"
                                style={{
                                  minWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  maxWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  // minHeight: `${Global.getRowHeight(1)}px`,
                                  // maxHeight: `${Global.getRowHeight(1)}px`,
                                  minHeight: `${Global.getRowHeight(col.rows ?? col.depth ?? 1, undefined, true) + 16}px`,
                                  maxHeight: `${Global.getRowHeight(col.rows ?? col.depth ?? 1, undefined, true) + 16}px`,
                                  ...getForeColorStyle(col),
                                }}
                              >
                                {String(getDisplayValue(col, editValues[col.celldata]) ?? "").trim() !== "" ? getDisplayValue(col, editValues[col.celldata]) : <br />}
                              </div>
                            )
                          ) : (
                            isFormEditing ? (
                              // テキストエリア項目入力
                              <textarea
                                // value={editValues[col.celldata] ?? ""}
                                value={getFormValue(col)}
                                style={{
                                  minWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  maxWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  // minHeight: `${Global.getRowHeight(col.rows ?? 1)}px`,
                                  // maxHeight: `${Global.getRowHeight(col.rows ?? 1)}px`,
                                  minHeight: `${Global.getRowHeight(col.rows ?? col.depth ?? 1)}px`,
                                  maxHeight: `${Global.getRowHeight(col.rows ?? col.depth ?? 1)}px`,
                                  paddingTop: "4px",
                                  lineHeight: "1.2",
                                  resize: "none",
                                  ...getForeColorStyle(col),
                                }}
                                onChange={(e) => handleEditValueChange(col.celldata, e.target.value)}
                                // readOnly={props.base_filters && props.base_filters.hasOwnProperty(col.celldata)}
                                readOnly={isReadOnly(col)}
                              />
                            ) : (
                              // テキストエリア項目をラベル表示
                              <div className="spread-form-label spread-form-label-multiline"
                                style={{
                                  minWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  maxWidth: `${Global.getColumnWidth(col.cols ?? col.length ?? 0)}px`,
                                  minHeight: `${Global.getRowHeight(col.rows ?? col.depth ?? 1, undefined, true) + 16}px`,
                                  maxHeight: `${Global.getRowHeight(col.rows ?? col.depth ?? 1, undefined, true) + 16}px`,
                                  paddingTop: "4px",
                                  lineHeight: "1.2",
                                  resize: "none",
                                  whiteSpace: "pre-wrap",
                                  ...getForeColorStyle(col),
                                }}
                              >
                                {String(getDisplayValue(col, editValues[col.celldata]) ?? "").trim() !== "" ? getDisplayValue(col, editValues[col.celldata]) : <br />}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    ))}
                  </div>
                ))}
              </div>
            </form>
          )}
        </div>
      )}

      {/* オーバーレイ子画面 */}
      {(childSpread || (childFilter && childFilter.__action)) && (
        <div className="spreadtable-overlay" ref={overlayRef}>
          {childSpread && (
            <SpreadTable
              spread={childSpread}
              // base_filters={childFilter?.base_filters}
              // base_filters={childFilter}
              base_filters={childFilter?.base_filters}
              ui_filters={childFilter?.ui_filters}
              form_pkey={childFilter?.form_pkey ?? Global.FormPkeyState.NONE}
              // 子ページ限定のフラグで、form_pkeyがあるならダイレクトにフォームページを開くことを示す
              // 保存後リストへ戻る。及びリストへ戻るボタンを表示する
              // direct_form={!!childFilter?.form_pkey}
              direct_form={childFilter?.form_pkey !== Global.FormPkeyState.NONE}
              onClose={closeChild}
            />
          )}
          {childFilter?.__action?.action_type === "voicerecording" && (
            <>
              {console.log('🔵 VoiceRecording props:', childFilter)}
              <VoiceRecording
                actionPkey={childFilter.__action.action_pkey}
                onClose={closeChild}
              />
            </>
          )}
          {childFilter?.__action?.action_type === "aisupport" && (
            <>
              {/* {(() => {
                alert(`🔵 AiSupport props: ${JSON.stringify(childFilter.__action.action_filters, null, 2)}`);
              })()} */}
              {/* もともとの状態 */}
              {/* <AiSupport
                相談支援GUID={childFilter.記録庫GUID}
                onClose={closeChild}
              /> */}
              <AiSupport
                actionTemplate={childFilter?.__action.action_template}
                actionPkey={childFilter?.__action.action_pkey}
                onClose={closeChild}
              />
              {/* <AiSupport
                action_filters={resolveActionFilters(row, childFilter.__action)}
                onClose={closeChild}
              /> */}


            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SpreadTable;
